/**
 * ShareLinksDialog — manage all minted share-links for a single file/folder.
 *
 * Opened from DriveView's right-click → Share. Shape mirrors a Discord
 * invite manager: list of active links, per-link controls (copy, edit
 * expiry/max-views/liveMode, revoke), and a "Create new link" panel that
 * picks the mode (App vs Public Link, Decision 5) and live/snapshot
 * (Decision 2) at mint time.
 *
 * One dialog handles BOTH share kinds — the list is shown unfiltered so
 * the owner sees every public + app link the same file has. Mode and
 * liveMode are surfaced per-row as badges.
 *
 * Analytics (per-link view-over-time, geo, etc.) lands in a follow-up; the
 * footer reserves the slot.
 */
import { useEffect, useLayoutEffect, useRef, useState, useCallback, useMemo, createContext, useContext, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Copy, X, Pencil, Ban, Plus, Folder, FolderInput, FolderTree, File, Mail, ChevronDown, AlertTriangle, Check, Eye, SquarePen, MoreVertical, Trash2, Link2, Lock, QrCode, Globe, Home } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useTheme, getThemeClass, useToast, useConfirm, useOrgMemberNames, GlassToggle, Tooltip, avatarBg, getInitials, initialFromEmail, nameFromEmail, PDAppIcon, FileIcon, useDraftState, useDraftInFlight } from '@papan/core-ui/ui'
import { isMiniappFolder, isReservedRootName, structuralFolderKind } from '@papan/types'
import { useBrand } from '@papan/brand/react'
import { getBrandSnapshot } from '@papan/brand/client'
import { ExtendAccessCard } from './ExtendAccessCard.js'
import { mintShareLink, listShareLinks, ensureDefaultLink, ensureDefaultAppLink, moveIntoOwnFolder, updateShareLink, revokeShareLink, sendShareLinkMagic, fetchShareCapabilities, fetchAppRootOptions, upsertRecipient, removeRecipient, previewShareExtend, extendShareAccess, fetchInboundReshares, ownerRevokeShareLink, type ShareLinkWithUrl, type ShareCapabilities, type AppRootOption, type RecipientGrant, type RecipientGrants, type InboundReshareLink } from '../client/index.js'
import { errorText, pinnedBatch } from '@papan/api-client'

/** The create panel's empty notify set — one instance, so the draft fallback is stable. */
const EMPTY_EMAILS: Set<string> = new Set()

/**
 * Stacking level for the dialog's PORTALED dropdowns (the permission picker and
 * the recipient-actions ⋮).
 *
 * These `createPortal` to <body>, so they do NOT inherit the dialog's stacking
 * context — their z-index competes with every other fixed layer in the app, and
 * must therefore clear ALL of this dialog's surfaces:
 *
 *     100  ShareLinksDialog overlay
 *     110  QrOverlay
 *     120  AddRecipientModal   ← PermissionPicker is used INSIDE this one too
 *     130  ...so the menus live here
 *
 * They used to sit at z-[60], a leftover from before the dialog was raised to
 * z-[100] (to clear the mobile header). The menu still opened and was still in
 * the DOM — it just painted BEHIND the dialog, so clicking "View (Last Saved)"
 * looked like it did nothing at all.
 *
 * 120 is not enough: `PermissionPicker` also renders inside AddRecipientModal
 * (z-[120]), and a menu at the same level as its own host loses on paint order.
 */
const SHARE_DIALOG_MENU_Z = 'z-[130]'

interface Props {
  fileId: string
  fileName: string
  /** Full path (e.g. "/papan/Projects/index.html"). Shown in the title's
   *  hover tooltip — mirrors the Share-to-Drive dialog. */
  filePath?: string
  isFolder: boolean
  /** Computed by the caller via isAppEligibleForFile(); gates the App mode pick. */
  isAppEligible: boolean
  onClose: () => void
}

// ─── Decision 10 helpers: derive legacy view from recipient_grants ───
//
// The dialog's UI still represents shares with a single permission +
// access mode + email list under the hood; recipient_grants is the
// data model. These helpers bridge until the full per-chip permission
// composer ships (TODO(decision-10-phase-35-polish)).

/** True iff the link has the "*" wildcard (Anyone with the link mode). */
/**
 * The link's primary permission. Decision 10 (+0083) has per-recipient
 * grant objects; for the dialog's row-summary chip we use the wildcard
 * entry if present (skipping 'block' — that's a deny, not a grant
 * level), else the first non-block email entry, else 'view'.
 */
function linkPrimaryPermission(grants: RecipientGrants): 'view' | 'edit' {
  const wildcard = grants['*']
  if (wildcard && wildcard.permission !== 'block') {
    return wildcard.permission
  }
  for (const k of Object.keys(grants)) {
    if (k === '*') continue
    const g = grants[k]!
    if (g.permission !== 'block') return g.permission
  }
  return 'view'
}

/**
 * Shallow-deep equality for two RecipientGrants maps. Used by EditPanel
 * to decide if the buffered grants differ from what's on the server,
 * driving the Save button's enabled state.
 */
function grantsEqual(a: RecipientGrants, b: RecipientGrants): boolean {
  const aKeys = Object.keys(a)
  const bKeys = Object.keys(b)
  if (aKeys.length !== bKeys.length) return false
  for (const k of aKeys) {
    const av = a[k]
    const bv = b[k]
    if (!bv) return false
    if (!av || av.permission !== bv.permission || av.liveMode !== bv.liveMode) return false
  }
  return true
}

/**
 * Whether the link's primary surface (wildcard, else first non-block
 * grant) currently broadcasts live edits. Used by LinkRow's Live badge.
 */
function linkPrimaryLiveMode(grants: RecipientGrants): boolean {
  const wildcard = grants['*']
  if (wildcard && wildcard.permission !== 'block') return wildcard.liveMode
  for (const k of Object.keys(grants)) {
    if (k === '*') continue
    const g = grants[k]!
    if (g.permission !== 'block') return g.liveMode
  }
  return false
}

// ─── Audience: WHO can open a link ───────────────────────────────────
//
// The chip on a link row carries two independent things: the AUDIENCE
// (who can open it — this type, rendered as the chip's text) and the
// PERMISSION (what they can do once open — rendered as the chip's icon).
// They used to share one slot, so a public link with a per-recipient
// override badged "View" and its audience vanished from the row.
//
// This is the SINGLE derivation; the row chip, the General-access label,
// and both link filters (LinksPage, WhoHasAccessDialog) all read it, so a
// filter can't disagree with the badge it filters on.

export type LinkAudience = 'public' | 'restricted' | 'private'

/**
 * Derive a link's audience from its recipient grants.
 *
 *   - `public`     — the `"*"` wildcard admits anyone holding the link.
 *   - `restricted` — no wildcard, but named recipients: only they get in.
 *   - `private`    — no wildcard and nobody named. The link grants NO new
 *                    access at all; it opens only for people who ALREADY
 *                    hold permission on the folder (the native floor —
 *                    same-tenant members AND cross-tenant `.shared`
 *                    recipients). See shareLinkCarriesNativeFloor.
 *
 * A wildcard stored as `'block'` is a deny, not public access, so it reads
 * as no wildcard. Mirrors `shareLinkNamesNobody` in @papan/database —
 * keep the two in step.
 */
export function linkAudience(grants: RecipientGrants, strictAudience?: boolean): LinkAudience {
  const wildcard = grants['*']
  if (wildcard && wildcard.permission !== 'block') return 'public'
  // 0249: a STRICT link (every link minted since) is Restricted even with nobody listed — it opens
  // for its creator and the list only, never through a visitor's own access.
  if (strictAudience) return 'restricted'
  const listed = Object.entries(grants).some(
    ([k, g]) => k !== '*' && g.permission !== 'block',
  )
  return listed ? 'restricted' : 'private'
}

/**
 * Not Public and nobody listed — what a STRICT (every new) link reads as Restricted with
 * an empty list. Such a link opens for its creator only, duplicating the target's Private
 * link, so Create stays disabled until someone is named (owner, 2026-10-03). Judged on the
 * grants alone, deliberately WITHOUT the strict flag (which would hide the empty list).
 */
export function linkNamesNobody(grants: RecipientGrants): boolean {
  return linkAudience(grants) === 'private'
}

/**
 * The grants a new link is MINTED with. An app link is view-only (Q8) and, since #1080, never
 * live — it shows the SAVED files — so a Source-mode Edit or "View (Live Edit)" choice isn't
 * carried into it (a block stays a block). The draft keeps the choice — switching back to
 * Source still shows it; only what is minted changes. The server stores the same
 * (`coerceGrantsForKind`).
 */
export function grantsForMint(grants: RecipientGrants, runAsApp: boolean): RecipientGrants {
  if (!runAsApp) return grants
  const out: RecipientGrants = {}
  for (const [k, v] of Object.entries(grants)) {
    out[k] = { ...v, permission: v.permission === 'edit' ? 'view' : v.permission, liveMode: false }
  }
  return out
}

export const AUDIENCE_LABEL: Record<LinkAudience, string> = {
  public: 'Public',
  restricted: 'Restricted',
  private: 'Private',
}

/** One sentence per audience — the chip tooltip and the General-access hint
 *  say the same thing, so the list and the editor can't drift. "Private"
 *  deliberately says what it is NOT (shared with someone new) rather than
 *  "only you": it can be the widest audience after Public, since everyone
 *  with folder permission — including whole partner orgs — can open it. */
export const AUDIENCE_HINT: Record<LinkAudience, string> = {
  public: 'Anyone with the link can open it.',
  restricted: 'Only the people you list can open it.',
  private: 'Not shared with anyone new — opens only for people who already have permission.',
}

export const AUDIENCE_CHIP_CLS: Record<LinkAudience, string> = {
  public: 'bg-blue-500/30 text-blue-200 [.theme-light_&]:text-blue-700',
  restricted: 'bg-purple-500/30 text-purple-200 [.theme-light_&]:text-purple-700',
  private: 'bg-indigo-500/30 text-indigo-200 [.theme-light_&]:text-indigo-700',
}

// ─── Avatar helpers — single source in @papan/core-ui/ui (so the taskbar
//     AvatarMenu, this dialog, and WhoHasAccess all share one palette + hash).
//     Re-exported for back-compat with WhoHasAccessDialog / LinksPage. ───────
export { avatarBg, initialFromEmail, nameFromEmail }

/**
 * A recipient's display name: the real account name when they are a member of
 * this org, otherwise one derived from the email.
 *
 * `nameFromEmail` only capitalises the local-part, so "kok2@…" becomes "Kok2"
 * for an account genuinely named "Kok 2" — wrong rather than terse, and
 * indistinguishable on screen from a real one. Outsider recipients have no
 * account, so the derived name is all there is for them and this returns it
 * unchanged.
 *
 * Initials come from whatever this returns, which is how the same person ends
 * up with the same glyph here as in the permission dialog.
 */
function recipientDisplayName(email: string, memberNames?: Map<string, string>): string {
  return memberNames?.get(email.toLowerCase()) || nameFromEmail(email)
}

/**
 * Org member names, provided by ShareLinksDialog and read by the recipient
 * rows five levels down (dialog → row → edit panel → options body → list).
 *
 * A context rather than five added props: threading it by hand means eight
 * edit sites and one forgotten hop away from a row that silently keeps the
 * wrong name. The default is `undefined`, so any consumer rendered outside a
 * provider — LinksPage renders the same row component — simply falls back to
 * the derived name, which is what it does today.
 */
const MemberNamesContext = createContext<Map<string, string> | undefined>(undefined)
const useMemberNames = () => useContext(MemberNamesContext)

/**
 * Sub-file reshare context (2026-07-29 plan — RESHARE_SUBFILE, Phase 6).
 *
 * A file/folder browsed INSIDE a `.shared`/`.chain` mount is addressed by the
 * compound id `s_<mountPointerId>_<sourceFileId>` (mount-listing.ts synthesizes
 * every child with `id: deriveMountFileId(...)`). The dialog receives that
 * compound id as `fileId`. Resharing it requires the mint to send:
 *   - `rootFileId = mountPointerId` — the `.shared` pointer ROW in the CALLER's
 *     tenant, so the `wrong_tenant` mint guard holds and the FK is satisfied.
 *   - `subSourceFileId = sourceFileId` — the SOURCE-tenant file the link is
 *     narrowed to.
 * Sending the bare source id (as the old default-link path did) 403s
 * `wrong_tenant` because the source file lives in another tenant.
 *
 * This SINGLE-LAYER parse mirrors `parseMountFileId` (shared/database/
 * mount-id.ts) exactly: prefix `s`, a `_`-free alphanumeric mount id, and a
 * non-empty (otherwise unrestricted) source remainder. It's INLINED rather than
 * imported because `@papan/database` is server-only (same reason HtmlViewer.tsx
 * inlines `mountSourceFileId`). A bare 20-char file id has no `_`, and the
 * tenant root `papan_<tenant>` fails the `s` prefix — so this only ever matches a
 * genuine mount child; normal shares parse to null and are completely unchanged.
 *
 * For a NESTED mount (chain→shared), `sourceFileId` is itself a compound id; the
 * server's `validateSubSourceScope` fails it closed (a compound id isn't a real
 * source row) — matching the plan's documented single-layer-only limitation.
 */
function parseMountChildId(id: string): { mountPointerId: string; sourceFileId: string } | null {
  const i1 = id.indexOf('_')
  if (i1 < 0 || id.slice(0, i1) !== 's') return null
  const i2 = id.indexOf('_', i1 + 1)
  if (i2 < 0) return null
  const mountPointerId = id.slice(i1 + 1, i2)
  const sourceFileId = id.slice(i2 + 1)
  if (mountPointerId === '' || sourceFileId === '') return null
  if (!/^[A-Za-z0-9]+$/.test(mountPointerId)) return null
  return { mountPointerId, sourceFileId }
}

/** Per link: bumped by a successful extension here, so the card's "can be extended" chip re-asks. */
const extendEpochs = new Map<string, number>()
function bumpExtendEpoch(id: string): void { extendEpochs.set(id, (extendEpochs.get(id) ?? 0) + 1) }

export function ShareLinksDialog({ fileId, fileName, filePath, isFolder, isAppEligible, onClose }: Props) {
  // Sub-file reshare context: non-null iff `fileId` is a `.shared`/`.chain`
  // mount child (see parseMountChildId). Drives every mount-only branch below;
  // null for normal shares, which keep their exact existing behavior. Memoized
  // so its object identity is stable across renders (used in effect deps).
  const mountChild = useMemo(() => parseMountChildId(fileId), [fileId])
  // Management calls (capabilities / list) key off the mount POINTER row (a real
  // caller-tenant row) when resharing mount content — the compound id isn't a row,
  // so those endpoints would fail on it. The CREATE mint additionally narrows via
  // subSourceFileId. Normal shares use `fileId` unchanged.
  const mgmtFileId = mountChild ? mountChild.mountPointerId : fileId
  // App-kind sub-file reshare is deferred (Phase 2b: `/app` rejects a mount-pointer
  // root as not-app-eligible), so mount content is QUICK_VIEW only — the App toggle
  // and the App/Source default card are suppressed.
  // App-kind sub-file reshare (RESHARE_SUBFILE Phase 2b) is now supported, so mount content
  // keeps App eligibility — the Create panel's App|Source toggle is shown and `handleCreate`
  // mints an `app` reshare (kind='app' + subSourceFileId). The immutable DEFAULT app link
  // still can't carry a sub-scope, so DefaultLinkSection stays suppressed for mount content
  // (canCreate={!mountChild}); App reshares are explicit Create-panel links only.
  const isAppEligibleEff = isAppEligible
  const toast = useToast()
  const confirm = useConfirm()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [links, setLinks] = useState<ShareLinkWithUrl[]>([])
  // External reshares of this content (owner/admin only) — outsiders who
  // re-shared it by link. Shown alongside the owner's own links.
  const [inboundReshares, setInboundReshares] = useState<InboundReshareLink[]>([])
  const [loading, setLoading] = useState(true)
  // URL of the link whose QR is being shown in the centered QR modal (null = closed).
  const [qrModalUrl, setQrModalUrl] = useState<string | null>(null)
  const [creatingLocal, setCreating] = useState(false)
  // #897 — the dialog's unsaved work (which link's panel is open, the new link
  // being composed) is kept across a phone/desktop layout swap, keyed by the
  // file (a mount child's compound id names the sub-item).
  const draftBase = `share:${fileId}`
  // The mint is marked in flight in the draft store too: a create panel a swap
  // restored mid-mint must not mint a second link (and e-mail its recipients
  // again) while the first is still running.
  // A mint that finished on the screen a swap replaced updated THAT dialog:
  // this one shows the new link and resets the form that came back filled in.
  const [createInFlight, trackCreate] = useDraftInFlight<ShareLinkWithUrl | null>(`${draftBase}:create`, (o) => {
    if (!o.ok || !o.value) return
    const created = o.value
    setLinks((prev) => (prev.some((l) => l.id === created.id) ? prev : [created, ...prev]))
    resetCreateForm()
  })
  const creating = creatingLocal || createInFlight
  const [editingId, setEditingId] = useDraftState<string | null>(`${draftBase}:editing`, null)
  const [busyId, setBusyId] = useState<string | null>(null)
  // Capability gates from the server. canShare === false hides the
  // Create panel and replaces it with a "no permission" message.
  // canGrantEdit === false removes the Edit option from the permission
  // picker. Mirrors the server-side gate in /app and /quick-view.
  const [capabilities, setCapabilities] = useState<ShareCapabilities | null>(null)
  // Mode + liveMode chosen for the NEXT mint.
  //   runAsApp default = true iff the file is App-eligible (HTML / .miniapp);
  //   off = mint as a plain ppn.gg public link.
  //   liveMode default = false (latest save) — opt-in only.
  const [runAsApp, setRunAsApp] = useDraftState<boolean>(`${draftBase}:create:runAsApp`, isAppEligibleEff)
  const [createExpiresLocal, setCreateExpiresLocal] = useDraftState<string>(`${draftBase}:create:expires`, '')
  // Decision 10 (+0083): unified recipient_grants map. Keys are emails
  // or the "*" wildcard ("Anyone with the link"). Each value carries
  // `permission` ('view'/'edit'/'block') AND per-recipient `liveMode`.
  // The dialog renders a GDrive-style General access section (the "*"
  // entry) + People list (emails) with a permission dropdown on each row.
  // The folder's NATIVE floor lives in its own auto-created default link
  // (is_default, pinned top, immutable). A link created HERE via the Create
  // button is an ADDITIONAL link — default = anyone with link / view / saved.
  const [createRecipientGrants, setCreateRecipientGrants] = useDraftState<RecipientGrants>(`${draftBase}:create:grants`, {
    '*': { permission: 'view', liveMode: false },
  })
  // App root selection for kind='app' + loose HTML files. NULL = let
  // the server default to the file's parent folder.
  const [createAppRootFileId, setCreateAppRootFileId] = useDraftState<string | null>(`${draftBase}:create:appRoot`, null)
  // Recipients flagged in the add-recipient modal to receive a quick-link
  // email AFTER the link mints. Create mode can't send the email at modal
  // submit time because the link doesn't exist yet; we fan out to each
  // staged email inside handleCreate once mintShareLink resolves. Lookups
  // and additions are O(1) via Set.
  const [createNotifyEmails, setCreateNotifyEmails] = useDraftState<Set<string>>(`${draftBase}:create:notify`, EMPTY_EMAILS)
  // (0108/0119) Add-to-Drive controls staged for the next mint. Defaults
  // mirror the column defaults: allowed (forbid=false), auto-approve off.
  const [createAutoApproveView, setCreateAutoApproveView] = useDraftState<boolean>(`${draftBase}:create:autoView`, false)
  const [createAutoApproveEdit, setCreateAutoApproveEdit] = useDraftState<boolean>(`${draftBase}:create:autoEdit`, false)
  const [createForbidAddToDrive, setCreateForbidAddToDrive] = useDraftState<boolean>(`${draftBase}:create:forbid`, false)
  const stageNotifyEmail = useCallback((email: string, send: boolean) => {
    setCreateNotifyEmails((prev) => {
      const next = new Set(prev)
      if (send) next.add(email)
      else next.delete(email)
      return next
    })
  }, [])
  // Create panel is collapsed by default — the toolbar button toggles it.
  const [showCreate, setShowCreate] = useDraftState<boolean>(`${draftBase}:create:open`, false)
  // When true, the next collapse of the create panel skips the
  // grid-rows animation. Set after a successful mint so the panel
  // vanishes immediately; cleared the next time the user opens it.
  const [instantHideCreate, setInstantHideCreate] = useState(false)
  useEffect(() => {
    if (showCreate) setInstantHideCreate(false)
  }, [showCreate])
  // Id of the row to scroll-into-view + briefly highlight after a
  // successful create. Cleared after the flash so the row settles back
  // to its normal styling.
  const [highlightId, setHighlightId] = useState<string | null>(null)
  // Show revoked / expired / capped rows in the list. Default off — the
  // common case is "show me what's active right now." Off-state rows
  // stay in the DB (the token is permanently burned and can never be
  // reissued) and surface here for audit / "did I revoke that yet?".
  const [showInactive, setShowInactive] = useState(false)
  /**
   * email (lowercased) → the account's real display name, for recipients who
   * are members of this org.
   *
   * Without it a recipient is labelled by `nameFromEmail`, which can only
   * capitalise the local-part: an account actually called "Kok 2" renders as
   * "Kok2". That is a WRONG name, not an abbreviated one, and nothing on
   * screen says it was guessed.
   *
   * Same endpoint and same failure handling as WhoHasAccessDialog: a caller
   * outside the org gets 403 (the route requires membership), a personal
   * tenant has none, and either way the catch leaves the map empty and every
   * recipient falls back to the derived name — today's behaviour. Outsider
   * recipients have no account anywhere, so they keep the derived name even
   * on success; their real name is not something this system knows.
   */
  // Real names for org-member recipients, from the shared query — the same
  // one the drive listing and the project cards use, so a session fetches this
  // once rather than on every dialog open. Undefined until it resolves and
  // empty outside an org (the endpoint requires membership), both of which
  // fall back to the email-derived name.
  const { data: memberNames } = useOrgMemberNames()

  // Apply the showInactive toggle client-side too — `includeRevoked` on
  // the listShareLinks call only controls the revoked filter at the
  // server. Expired / capped rows are still alive in the DB and get
  // returned unconditionally; the dialog hides them by default to keep
  // the common "what's working right now?" view uncluttered.
  const visibleLinks = useMemo(() => {
    if (showInactive) return links
    const now = Date.now()
    return links.filter(link => {
      if (link.isRevoked) return false
      if (link.expiresAt && new Date(link.expiresAt).getTime() < now) return false
      return true
    })
  }, [links, showInactive])

  // Auto-created default native links are pinned + rendered as their own distinct
  // card (immutable, native-only); owner-created "extra" links list below. An
  // app-eligible target holds up to TWO defaults — the App URL (kind='app') and
  // the Source URL (kind='quick_view') — surfaced via a toggle in the card.
  // Mount content: the list is keyed on the mount POINTER, so it holds defaults for MANY
  // sub-files (+ the whole-mount default). Pick the default whose sub-scope matches THIS
  // sub-file. A normal share has no sub-scoped defaults, so `null` matches as before.
  const defaultScope = mountChild ? mountChild.sourceFileId : null
  // One default per target and kind (owner, 2026-10-03), listed to whoever may use it.
  const defaultAppLink = useMemo(() => visibleLinks.find(l => l.isDefault && l.kind === 'app' && (l.subSourceFileId ?? null) === defaultScope) ?? null, [visibleLinks, defaultScope])
  const defaultSourceLink = useMemo(() => visibleLinks.find(l => l.isDefault && l.kind === 'quick_view' && (l.subSourceFileId ?? null) === defaultScope) ?? null, [visibleLinks, defaultScope])
  const hasDefault = !!(defaultAppLink || defaultSourceLink)
  const extraLinks = useMemo(() => visibleLinks.filter(l => !l.isDefault), [visibleLinks])

  // 0083 amendment: the live/saved choice is no longer gated by file
  // type — folders, binaries, etc. all expose the variant since support
  // can be added later without re-shipping the picker. Per-recipient
  // liveMode lives inside recipient_grants.

  // The App default was REFUSED because the file sits directly in one of the drive's
  // own folders (#888/#889) — the card explains and offers the move instead of a link.
  // The target is one of the drive's own folders: never shared as a whole (the reason, or null).
  const [folderShareBlocked, setFolderShareBlocked] = useState<string | null>(null)
  const [appDefaultBlocked, setAppDefaultBlocked] = useState<{ folder: { id: string; name: string }; detail: string } | null>(null)
  // App default mint that keeps a structural refusal (any other failure: the Retry box).
  const ensureAppDefault = useCallback(async (): Promise<ShareLinkWithUrl | null> => {
    const r = await ensureDefaultAppLink(mgmtFileId, mountChild?.sourceFileId).catch(() => null)
    // Only a structural refusal THIS time blocks — any other answer clears an earlier one
    // (a failure shows Retry, never a Move from a folder the page may have left).
    setAppDefaultBlocked(r && !r.ok && r.structural ? { folder: r.folder, detail: r.detail } : null)
    return r?.ok ? r.link : null
  }, [mgmtFileId, mountChild])

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      // Ensure the auto-created default native link(s) before listing so they're
      // present + pinned on first open (idempotent). App-eligible targets show the
      // App default first (eager); their Source default is minted lazily when the
      // user switches the card's toggle to Source. Everything else gets the Source
      // (quick_view) default — now files too, not just folders.
      //
      // For a sub-file reshare (mount child) the default is minted against the mount POINTER
      // (mgmtFileId) + the source sub-file (subSourceFileId) — per-sub-file defaults are keyed
      // by it (0127), so each reshared sub-file gets its own App|Source defaults exactly like
      // the source owner. `mountChild?.sourceFileId` is undefined for a normal share.
      if (isAppEligibleEff) await ensureAppDefault()
      else {
        // One of the drive's own folders can't be shared as a whole: the server refuses
        // its Source default (`structural_folder`), and the dialog says so instead of
        // offering links that would be refused.
        const blocked = await ensureDefaultLink(mgmtFileId, 'quick_view', mountChild?.sourceFileId)
          .then(() => null)
          .catch((e: { code?: string; detail?: string }) => (e?.code === 'structural_folder' ? (e.detail || "This folder can't be shared as a whole.") : null))
        setFolderShareBlocked(blocked)
      }
      const [list, inbound] = await Promise.all([
        // Key list + inbound off the mount POINTER when resharing mount content
        // (the compound id isn't a real row). This surfaces the reshare links the
        // caller created for this mount (incl. this sub-file's).
        listShareLinks({ fileId: mgmtFileId, includeRevoked: showInactive }),
        // Non-owner callers get [] (server gates on admin+); failure is non-fatal.
        fetchInboundReshares(mgmtFileId).catch(() => [] as InboundReshareLink[]),
      ])
      // Pin the default native link to the top of the list.
      setLinks([...list].sort((a, b) => Number(b.isDefault) - Number(a.isDefault)))
      setInboundReshares(inbound)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to load share links')
    } finally {
      setLoading(false)
    }
  }, [fileId, mgmtFileId, mountChild, showInactive, toast, isAppEligibleEff, ensureAppDefault])

  useEffect(() => {
    void reload()
  }, [reload])

  // Fetch capability flags once per fileId. Errors silently default to
  // "cannot share / cannot grant edit" so the dialog fails closed.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        // Resharing mount content: the mount POINTER + the sub-item — the same
        // pair the mint receives, so the dialog answers what the mint will do
        // (#848: allow-sharing is asked AT the sub-item, not only the pointer).
        const caps = await fetchShareCapabilities(mgmtFileId, mountChild ? { subSourceFileId: mountChild.sourceFileId } : {})
        if (!cancelled) setCapabilities(caps)
      } catch {
        if (!cancelled) setCapabilities({ canShare: false, canGrantEdit: false })
      }
    })()
    return () => { cancelled = true }
  }, [mgmtFileId, mountChild?.sourceFileId])

  /** The create panel after a mint: collapsed (no animation) and back to its
   *  defaults, so the new row is the visible result. */
  const resetCreateForm = () => {
    setInstantHideCreate(true)
    setShowCreate(false)
    setCreateRecipientGrants({ '*': { permission: 'view', liveMode: false } })
    setCreateNotifyEmails(new Set())
    setCreateAutoApproveView(false)
    setCreateAutoApproveEdit(false)
    setCreateForbidAddToDrive(false)
  }

  const handleCreate = useCallback(async () => {
    if (creating) return
    setCreating(true)
    // #1316 review round 2 (owner, 2026-10-28): the create the person pressed FINISHES as the identity that pressed it —
    // the mint and the opted-in emails go as it, never as the next session; nothing is shown once that session ended
    const batch = pinnedBatch()
    await trackCreate(async () => { try {
      // Parse expiry from the local form value. Empty string → null (no
      // expiry); invalid value would have been blocked at input level.
      const expiresAtIso = createExpiresLocal ? localInputToIso(createExpiresLocal) : null

      // Decision 10 (+0083): recipient_grants carries permission +
      // per-recipient liveMode. App mode silently coerces edit → view
      // per-grant (Q8 defense in depth — UI already hides the Edit
      // option in App mode). 'block' is preserved as-is.
      // App: view-only and never live (#1080) — see grantsForMint.
      const mintGrants = grantsForMint(createRecipientGrants, runAsApp)

      // A private link with no "*" wildcard AND no recipients is a NATIVE link: it
      // carries the native floor (folder members can open it) and flips to Restricted
      // the moment a recipient is added — see shareLinkCarriesNativeFloor + the
      // Native/Restricted badge. That's a legitimate handout link (a separate,
      // editable token distinct from the immutable default), so minting it is allowed;
      // its creator opens it by identity (I107) — no entry is added for them.
      // Sub-file reshare (mount child): root the link at the mount POINTER in the
      // caller's tenant (passes the mint's `wrong_tenant` guard) and narrow it to
      // the source sub-file via `subSourceFileId`. App-kind reshare (Phase 2b) is now
      // supported — the /app route resolves eligibility + app-root against the SOURCE
      // sub-file, so `kind` follows the App|Source toggle just like a normal share.
      // Normal (non-mount) shares are completely unchanged.
      const mintKind = runAsApp ? 'app' : 'quick_view'
      const result = await batch.run(() => mintShareLink({
        fileId: mgmtFileId,
        kind: mintKind,
        expiresAt: expiresAtIso,
        recipientGrants: mintGrants,
        // App root: the server derives it from the source sub-file's parent for a reshare
        // (a source-tenant folder the client can't name), so send null for mount content.
        appRootFileId: mintKind === 'app' && !isFolder && !mountChild ? createAppRootFileId : null,
        ...(mountChild ? { subSourceFileId: mountChild.sourceFileId } : {}),
        // (0108/0119) Add-to-Drive controls — quick-view only (app shares
        // aren't mountable; the /app mint route ignores these anyway).
        ...(mintKind === 'app' ? {} : {
          autoApproveView: createAutoApproveView,
          autoApproveEdit: createAutoApproveEdit,
          forbidAddToDrive: createForbidAddToDrive,
        }),
      }))
      // Prepend the new row to the local cache instead of refetching
      // the full list — `reload()` toggles `loading` which flashes the
      // dialog body. The mint endpoint returns the same enriched shape
      // (creator, canEdit, displayToken) the list endpoint emits, so
      // the row renders correctly without a server round trip.
      const created = { ...result.shareLink, url: result.url } as ShareLinkWithUrl
      const shown = () => !batch.ended()
      if (shown()) setLinks((prev) => [created, ...prev])
      // Highlight the new row + scroll it into view. Cleared after the
      // flash so the row settles back to normal styling. LinkRow's own
      // effect kicks the scroll + animation when `isHighlighted` flips.
      if (shown()) setHighlightId(created.id)
      setTimeout(() => {
        setHighlightId((prev) => (prev === created.id ? null : prev))
      }, 2000)
      // Post-mint fan-out: send the quick-link email to every recipient
      // the user opted into via the modal checkbox. Drop any staged
      // entries that no longer correspond to a recipient (e.g. user
      // checked the box then removed the row before creating). Sends
      // fire in parallel; failures surface per-email but don't block.
      // ...and never a BLOCKED recipient. This is the guarantee, not the
      // disabled checkbox: the staged intent is recorded when the row is
      // added, so checking the box and THEN switching that row to Blocked
      // leaves a stale "yes, email them" behind — which is exactly how a
      // blocked person came to receive an invite to a link denying them
      // access. Reading the permission from the CREATED grants means it
      // reflects what was actually minted, not what the form remembered.
      const stagedActual = [...createNotifyEmails].filter(
        (e) => e in created.recipientGrants && created.recipientGrants[e]?.permission !== 'block',
      )
      if (stagedActual.length > 0) {
        await Promise.allSettled(
          stagedActual.map((e) =>
            batch.run(() => sendShareLinkMagic(created.id, e, { force: false })).then((res) => {
              if (!res.ok && shown()) {
                toast.error(`Couldn't email ${e}${res.kind === 'rate-limited' ? ' (rate-limited)' : `: ${res.error}`}`)
              }
            }),
          ),
        )
      }
      // The link is minted and any opted-in recipients have already been
      // emailed — collapse the create panel without animation and reset
      // the form back to its defaults so the new row is the visible
      // result.
      if (!shown()) return created
      resetCreateForm()
      toast.success(
        stagedActual.length > 0
          ? `Created — emailed ${stagedActual.length} ${stagedActual.length === 1 ? 'recipient' : 'recipients'}.`
          : 'Link created',
      )
      return created
    } catch (err: unknown) {
      const e = err as { error?: string; message?: string; detail?: string } | undefined
      if (!batch.ended()) toast.error(`Couldn't create link: ${e?.detail ?? e?.error ?? e?.message ?? 'Unknown error'}`)
      return null
    } finally {
      setCreating(false)
    } })
  }, [creating, trackCreate, fileId, mgmtFileId, mountChild, runAsApp, createExpiresLocal, createRecipientGrants, toast, createAppRootFileId, isFolder, createNotifyEmails, createAutoApproveView, createAutoApproveEdit, createForbidAddToDrive])

  // Open the create panel. Grants start EMPTY — no "*" wildcard — so the
  // panel's audience selector lands on Restricted and the new link cannot be
  // public by accident. Publishing is a deliberate second act inside the
  // panel, not a consequence of which button was clicked out here: the old
  // pair of Public/Private buttons made "anyone with the link" one click from
  // the list, with the consequence stated only in a hover title.
  const openCreate = useCallback(() => {
    setCreateRecipientGrants({})
    // Reset the rest of the staged form to defaults so a prior attempt's
    // expiry / app-root / Add-to-Drive / notify choices don't bleed into this
    // new link (reopening the panel is common).
    setCreateExpiresLocal('')
    setCreateAppRootFileId(null)
    setCreateAutoApproveView(false)
    setCreateAutoApproveEdit(false)
    setCreateForbidAddToDrive(false)
    setCreateNotifyEmails(new Set())
    setShowCreate(true)
  }, [])

  const handleCopy = useCallback(async (url: string) => {
    try {
      await navigator.clipboard.writeText(url)
      toast.success('Link copied to clipboard')
    } catch {
      toast.info(`Link: ${url}`)
    }
  }, [toast])

  const handleRevoke = useCallback(async (link: ShareLinkWithUrl) => {
    const ok = await confirm({
      title: 'Revoke this link?',
      message: (
        <span>
          <code>{link.url}</code> will stop working immediately — anyone visiting it sees a 404.
          The token is permanently burned: even a future link on the same file gets a fresh, unrelated URL.
          This can't be undone.
        </span>
      ),
      type: 'danger',
      confirmText: 'Revoke',
    })
    if (!ok) return
    setBusyId(link.id)
    try {
      await revokeShareLink(link.id)
      // Close the settings panel if the revoke came from inside it. A revoked
      // card no longer renders the toggle (nothing left to configure), so an
      // open panel would have no way to collapse. Only reachable with "Show
      // inactive" on — otherwise the card drops out of the list and unmounts.
      // LinksPage's revoke already does this; the two now match.
      setEditingId((cur) => (cur === link.id ? null : cur))
      toast.success('Link revoked')
      await reload()
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke')
    } finally {
      setBusyId(null)
    }
  }, [confirm, reload, toast])

  // Owner-revoke of an EXTERNAL reshare link on this content (different tenant
  // from the link). Only kills the link's view access — a recipient who already
  // added it to their Drive keeps their separate owner→recipient grant.
  const handleOwnerRevoke = useCallback(async (r: InboundReshareLink) => {
    const who = r.reshareFrom.name || r.reshareFrom.email || r.reshareFrom.tenantName
    const ok = await confirm({
      title: 'Revoke this reshare?',
      message: (
        <span>
          The link <strong>{who}</strong> created to re-share your content will stop working.
          Anyone who already added it to their Drive keeps the access you granted — revoke that
          separately under “Who has access”.
        </span>
      ),
      type: 'danger',
      confirmText: 'Revoke reshare',
    })
    if (!ok) return
    setBusyId(r.id)
    try {
      await ownerRevokeShareLink(r.id)
      setInboundReshares((prev) => prev.filter((x) => x.id !== r.id))
      toast.success('Reshare revoked')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to revoke')
    } finally {
      setBusyId(null)
    }
  }, [confirm, toast])

  // EditPanel now owns the PATCH so it can auto-save before a Send
  // Email action. The parent only learns about row updates via the
  // notification callback below.
  const handleLinkPatched = useCallback((updated: ShareLinkWithUrl) => {
    // Merge, don't replace: the save responses carry the link's fields but not the
    // caller-specific flags the list computed (`canRepoint`, `canEdit`) — keep them.
    setLinks((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)))
  }, [])
  // An external reshare row was edited — merge the patched link fields back,
  // preserving the `reshareFrom` attribution the card needs.
  const handleInboundPatched = useCallback((updated: ShareLinkWithUrl) => {
    setInboundReshares((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)))
  }, [])
  const handleEditClose = useCallback(() => {
    setEditingId(null)
  }, [])

  // Portal to <body>: rendered inline, this `fixed inset-0` is trapped in
  // the editor's stacking context (.ws-card z-1 → .ws-editor-toolbar z-2),
  // so the code editor's transparent input textarea + highlighted gutter
  // paint OVER the dialog even at z-50 (the mobile "line numbers show
  // through the share dialog" bug). At body level z-50 competes globally.
  // The panel carries its own `themeClass`, so it themes correctly outside
  // any ThemedArea (same as the nested QR/confirm portal below).
  return createPortal(
    // z-[100]: above the mobile header (z-[60]) so the overlay covers the full
    // viewport on phones (desktop unaffected — nothing sits in 50–100 there).
    //
    // THE DIALOG'S Z-STACK — anything portaled out of this tree must respect it:
    //   100  this overlay
    //   110  QrOverlay
    //   120  AddRecipientModal
    //   130  SHARE_DIALOG_MENU_Z — the portaled dropdowns (see below)
    <MemberNamesContext.Provider value={memberNames}>
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Lighter dim than the classic /50 — the frosted card needs visible
          content behind it for its blur to read as glass. */}
      <div
        className="fixed inset-0 bg-black/20 ws-anim-fade-in"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
        role="button"
        tabIndex={-1}
        aria-label="Close"
      />
      {/* ws-glass-modal (DESIGN_DOC §10) frosts the dialog surface itself;
          the inner cards/panels keep their themed fills and float on the
          glass. Radius/shadow/overflow come from the class. */}
      <div className={`relative z-10 max-w-2xl w-full max-h-[85vh] flex flex-col ${themeClass} ws-glass-modal ws-anim-modal-in`}>

        {/* Header — mirrors the Share-to-Drive dialog */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4">
          <h3 className="text-lg font-semibold themed-text flex items-center gap-1.5 flex-wrap min-w-0">
            <span>Share links for</span>
            <Tooltip content={filePath ?? ''} always followCursor>
              <span className="inline-flex items-center gap-1.5 themed-text-secondary font-normal min-w-0">
                {isFolder && isMiniappFolder(fileName)
                  ? <PDAppIcon size={18} className="text-pink-500 shrink-0" />
                  : isFolder
                    ? <Folder size={18} className="text-yellow-500 shrink-0" />
                    : <File size={18} className="themed-text-muted shrink-0" />}
                <span className="truncate">{fileName}</span>
              </span>
            </Tooltip>
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 themed-text-muted hover:themed-text rounded shrink-0 ml-3"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 size={20} className="animate-spin themed-text-muted" />
            </div>
          ) : (
            <>
              {/* The auto-created default native link(s) — their OWN distinct card,
                  pinned above the "Create more link" action. Immutable, native-only.
                  App-eligible ⇒ App|Source toggle (App first); else a single card. */}
              {folderShareBlocked ? <ForbiddenFolderCard detail={folderShareBlocked} /> : <DefaultLinkSection
                isAppEligible={isAppEligibleEff}
                appLink={defaultAppLink}
                sourceLink={defaultSourceLink}
                // Only offer to (re)mint a missing APP default when the caller can share —
                // otherwise a Retry would just 403. The SOURCE default needs no sharing right
                // — DefaultLinkSection always offers it. Mount content (sub-file reshare) now has
                // per-sub-file defaults (0127), so the App|Source card + toggle render exactly
                // like the source owner's.
                canCreate={capabilities?.canShare !== false}
                onCopy={(url) => handleCopy(url)}
                onShowQr={(url) => setQrModalUrl(url)}
                appBlocked={appDefaultBlocked}
                onMoveIntoOwnFolder={!mountChild && appDefaultBlocked ? async () => {
                  // A folder named after the page, beside it — then mint the App default
                  // again: the page now has an app folder of its own.
                  // Never a reserved name — `Library.html` at the drive root would ask for a
                  // folder the create guard refuses.
                  const base = fileName.replace(/\.(html?|htm)$/i, '') || fileName
                  const name = isReservedRootName(base) ? `${base} app` : base
                  try {
                    const moved = await moveIntoOwnFolder(fileId, appDefaultBlocked.folder.id, name)
                    toast.success(`Moved into its own folder “${moved.folderName}”.`)
                    // The page has left that folder: whatever the re-mint says next, the
                    // old refusal (and a second Move from the same folder) no longer applies.
                    setAppDefaultBlocked(null)
                    await reload()
                  } catch (e) {
                    toast.error(e instanceof Error ? e.message : "Couldn't move the file.")
                  }
                } : undefined}
                onEnsure={async (kind) => {
                  // Async-load a default of `kind` and splice it into the list in
                  // place — a full reload() flips `loading`, which unmounts the
                  // DefaultLinkSection and resets its tab (the flash). Used for the
                  // lazy Source mint AND to retry a failed eager App mint. For mount content
                  // the default is keyed on the mount POINTER + the source sub-file.
                  const link = kind === 'app'
                    ? await ensureAppDefault()
                    : await ensureDefaultLink(mgmtFileId, kind, mountChild?.sourceFileId).catch(() => null)
                  if (link) setLinks((prev) => (prev.some((l) => l.id === link.id)
                    ? prev
                    : [...prev, link].sort((a, b) => Number(b.isDefault) - Number(a.isDefault))))
                }}
              />}
              <div className="mb-3 mt-4">
                {/* Count owner-created "extra" links + external reshares; the
                    default link is shown separately above.

                    The default-link-only case renders NOTHING. A tally exists to
                    say how much is below it, and there "Default link only"
                    described a card the user is already looking at — restating
                    it turned the empty state into a line of furniture. Silence
                    reads as "nothing here yet", which is the truth. */}
                {(() => {
                  const extra = extraLinks.length + inboundReshares.length
                  // An HTML page / PDApp has TWO private links (App + Source — the Source one is
                  // minted when its tab is first opened); anything else has one.
                  const privateCount = hasDefault ? (isAppEligibleEff ? 2 : 1) : 0
                  const total = extra + privateCount
                  let text: string | null = null
                  if (total === 0) text = `No share links yet for this ${isFolder ? 'folder' : 'file'}.`
                  else if (extra === 0) text = null
                  else if (!hasDefault) text = `${total} ${total === 1 ? 'link' : 'links'}`
                  else text = `${total} links · ${privateCount} private + ${extra} more`
                  // Skip the <p> entirely rather than rendering an empty one —
                  // its mb-2 would leave a phantom gap above the create button.
                  // Secondary (not muted): muted vanished on the glass over a white page.
                  return text && <p className="text-xs themed-text-secondary mb-2">{text}</p>
                })()}
                {!showCreate && capabilities?.canShare !== false && !folderShareBlocked && (
                  // ONE create button. The audience (Public / Restricted) is
                  // chosen inside the panel, defaulting to Restricted — see
                  // openCreate for why the choice moved off this row.
                  <button
                    type="button"
                    onClick={openCreate}
                    disabled={creating || capabilities == null}
                    className="themed-btn-primary w-full inline-flex items-center justify-center gap-1.5 px-3 py-2.5 text-xs font-semibold rounded-lg disabled:opacity-50"
                    title={capabilities == null ? 'Checking permissions…' : 'Create a share link — you choose who can open it'}
                  >
                    <Plus size={14} />
                    Create new link
                  </button>
                )}
                {capabilities?.canShare === false && (
                  <span className="text-xs text-yellow-300/80">
                    You don't have permission to create share links here.
                  </span>
                )}
              </div>
              <CollapsibleEdit isOpen={showCreate} allowOverflowWhenOpen instantClose={instantHideCreate}>
                <CreatePanel
                  isAppEligible={isAppEligibleEff}
                  // The page's folder is one of the drive's own: PDApp stays offered (owner:
                  // show it exists) but can't be created from here — its Create is disabled
                  // with the reason; "Share source code" still works.
                  appBlocked={!!appDefaultBlocked}
                  isFolder={isFolder}
                  canGrantEdit={capabilities?.canGrantEdit ?? false}
                  runAsApp={runAsApp}
                  setRunAsApp={setRunAsApp}
                  createExpiresLocal={createExpiresLocal}
                  setCreateExpiresLocal={setCreateExpiresLocal}
                  createRecipientGrants={createRecipientGrants}
                  setCreateRecipientGrants={setCreateRecipientGrants}
                  createAppRootFileId={createAppRootFileId}
                  setCreateAppRootFileId={setCreateAppRootFileId}
                  createAutoApproveView={createAutoApproveView}
                  setCreateAutoApproveView={setCreateAutoApproveView}
                  createAutoApproveEdit={createAutoApproveEdit}
                  setCreateAutoApproveEdit={setCreateAutoApproveEdit}
                  createForbidAddToDrive={createForbidAddToDrive}
                  setCreateForbidAddToDrive={setCreateForbidAddToDrive}
                  fileId={fileId}
                  creating={creating}
                  stageNotifyEmail={stageNotifyEmail}
                  notifyEmails={createNotifyEmails}
                  onCancel={() => {
                    setShowCreate(false)
                    setCreateNotifyEmails(new Set())
                  }}
                  onCreate={handleCreate}
                />
              </CollapsibleEdit>
              {(extraLinks.length > 0 || inboundReshares.length > 0) && (
                <div className="space-y-2 mt-4">
                  {extraLinks.map((link) => (
                    <LinkRow
                      key={link.id}
                      link={link}
                      isEditing={editingId === link.id}
                      isBusy={busyId === link.id}
                      isFolder={isFolder}
                      canGrantEdit={capabilities?.canGrantEdit ?? false}
                      isHighlighted={highlightId === link.id}
                      onStartEdit={() => setEditingId(link.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onCopy={() => handleCopy(link.url)}
                      onShowQr={setQrModalUrl}
                      onRevoke={() => handleRevoke(link)}
                      onClose={handleEditClose}
                      onLinkPatched={handleLinkPatched}
                    />
                  ))}
                  {/* External reshares render as ordinary cards in the same list,
                      marked only by the outsider avatar glyph — owner-revoke +
                      owner edit endpoints. */}
                  <InboundResharesSection
                    reshares={inboundReshares}
                    isFolder={isFolder}
                    editingId={editingId}
                    busyId={busyId}
                    onStartEdit={setEditingId}
                    onCancelEdit={() => setEditingId(null)}
                    onCopy={handleCopy}
                    onShowQr={setQrModalUrl}
                    onRevoke={handleOwnerRevoke}
                    onLinkPatched={handleInboundPatched}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {/* ws-glass-footer — a light lift over the glass, separated from the
            body by a soft upward shadow instead of a hairline. */}
        <div className="px-5 py-3 ws-glass-footer text-xs themed-text-muted flex items-center justify-between gap-3">
          <button
            type="button"
            role="switch"
            aria-checked={showInactive}
            onClick={() => setShowInactive(!showInactive)}
            className="flex items-center gap-2 shrink-0 cursor-pointer"
          >
            <Switch checked={showInactive} />
            <span>Show inactive (revoked / expired / capped)</span>
          </button>
          <span className="hidden sm:inline truncate">Per-link analytics coming soon.</span>
        </div>

        {/* QR modal — portaled fullscreen (blurs everything, dialog included).
            Opened by any link row's QR icon; shows a scannable code for that URL. */}
        <QrOverlay url={qrModalUrl} onClose={() => setQrModalUrl(null)} />
      </div>
    </div>
    </MemberNamesContext.Provider>,
    document.body,
  )
}

/** Centered QR modal for a link URL. Portaled to <body> — NOT nested in the
 *  host dialog — for two reasons: (1) the frosted ws-glass-modal is a backdrop
 *  root, so a nested backdrop-blur would silently no-op (see the ws-menu
 *  nesting note in index.css); (2) the point of the overlay is to blur
 *  EVERYTHING and focus the QR, dialog included. The panel itself is ws-menu
 *  glass (real frost at body level). Shared by ShareLinksDialog and
 *  WhoHasAccessDialog. themeClass goes on the PANEL, not the inset-0 wrapper
 *  (theme classes paint an opaque bg-primary — same trap as AddRecipientModal). */
/** A link's QR code on its white tile — the same drawing QrOverlay centres, for a surface that shows it in place
 *  (the bell's link review dialog: a portalled overlay would sit behind that modal's inert background). */
export function LinkQrCode({ url, size = 160 }: { url: string; size?: number }) {
  return (
    <div className="rounded-lg bg-white p-3 shadow-[0_2px_6px_-1px_rgba(30,27,75,0.2)]">
      <QRCodeSVG value={url} size={size} level="M" marginSize={0} />
    </div>
  )
}

export function QrOverlay({ url, onClose }: { url: string | null; onClose: () => void }) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  if (!url) return null
  return createPortal(
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/30 backdrop-blur-md ws-anim-fade-in"
        onClick={onClose}
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
        role="button"
        tabIndex={-1}
        aria-label="Close QR"
      />
      <div className={`relative z-10 ${themeClass} ws-menu max-w-[90%] ws-anim-modal-in`}>
        <div className="p-4 flex flex-col items-center gap-4">
          <div className="rounded-lg bg-white p-3 shadow-[0_2px_6px_-1px_rgba(30,27,75,0.2)]">
            <QRCodeSVG value={url} size={220} level="M" marginSize={0} />
          </div>
          <code className="text-[11px] themed-text-secondary font-mono break-all text-center max-w-[280px]">{url}</code>
        </div>
      </div>
    </div>,
    document.body,
  )
}

// ─── Row ─────────────────────────────────────────────────────────────

/** External reshares of this content — outsiders who re-shared it by link. Each
 *  renders as an ORDINARY share-link card (full LinkRow: pencil edit,
 *  recipients/expiry) — marked only by the neutral outsider avatar glyph (+ amber
 *  ! dot, resharer identity in its hover) in place of a personal creator avatar.
 *  Edit/revoke target the owner-authorized endpoints; Edit can't grant Edit
 *  (reshares stay read-only — only the owner sets read-write via the drive
 *  approval). Returns a fragment so it drops straight into the links list. */
export function InboundResharesSection({
  reshares, isFolder, editingId, busyId, onStartEdit, onCancelEdit, onCopy, onShowQr, onRevoke, onLinkPatched,
}: {
  reshares: InboundReshareLink[]
  isFolder: boolean
  editingId: string | null
  busyId: string | null
  onStartEdit: (id: string) => void
  onCancelEdit: () => void
  onCopy: (url: string) => void
  onShowQr?: (url: string) => void
  onRevoke: (r: InboundReshareLink) => void
  onLinkPatched: (updated: ShareLinkWithUrl) => void
}) {
  // Collapse ONLY the "Shared default link" rows (inbound DEFAULT reshares) — those pile up
  // once several `.shared` recipients each mint per-sub-file defaults. Explicit (non-default)
  // inbound reshares keep showing normally; only the auto-default clutter hides by default.
  const [collapsed, setCollapsed] = useState(true)
  if (reshares.length === 0) return null
  // The outsider marker (neutral glyph + amber ! dot, resharer identity in the hover) carries
  // the "external reshare" signal; a DEFAULT reshare additionally shows the "Shared default
  // link" badge (LinkRow, gated on link.isDefault).
  const renderRow = (r: InboundReshareLink) => (
    <LinkRow
      key={r.id}
      link={r}
      isFolder={isFolder}
      canGrantEdit={false}
      isEditing={editingId === r.id}
      isBusy={busyId === r.id}
      creatorShareOverlay
      creatorIsOrg={r.reshareFrom.isOrg}
      creatorOrgName={r.reshareFrom.tenantName}
      onStartEdit={() => onStartEdit(r.id)}
      onCancelEdit={onCancelEdit}
      onClose={onCancelEdit}
      onCopy={() => onCopy(r.url)}
      onShowQr={onShowQr}
      onRevoke={() => onRevoke(r)}
      onLinkPatched={onLinkPatched}
    />
  )
  const sharedDefaults = reshares.filter((r) => r.isDefault)
  const explicitReshares = reshares.filter((r) => !r.isDefault)
  return (
    <>
      {explicitReshares.map(renderRow)}
      {sharedDefaults.length > 0 && (
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="w-full flex items-center gap-1.5 text-xs themed-text-muted hover:themed-text py-1"
            aria-expanded={!collapsed}
          >
            <ChevronDown size={14} className={`transition-transform ${collapsed ? '-rotate-90' : ''}`} />
            <span className="font-medium">Shared default links</span>
            <span>· {sharedDefaults.length}</span>
          </button>
          {!collapsed && sharedDefaults.map(renderRow)}
        </div>
      )}
    </>
  )
}

/**
 * The folder's auto-created DEFAULT NATIVE LINK — rendered as its own distinct
 * card (indigo, "Native" chip), visually separate from owner-created link rows.
 * Immutable + native-only: copy is the only action (no edit / no revoke).
 */
/**
 * The default-link block above the "extra" links. For an app-eligible target it's
 * an App|Source toggle (App shown first) over the two native defaults; the Source
 * default is minted LAZILY the first time the user switches to it. Everything else
 * is a single "Private link" card. `onEnsureSource` mints + reloads.
 */
function DefaultLinkSection({ isAppEligible, appLink, sourceLink, canCreate, onCopy, onShowQr, onEnsure, appBlocked, onMoveIntoOwnFolder }: {
  isAppEligible: boolean
  appLink: ShareLinkWithUrl | null
  sourceLink: ShareLinkWithUrl | null
  /** The App default was refused: the page sits directly in one of the drive's own folders (#889). */
  appBlocked?: { folder: { id: string; name: string }; detail: string } | null
  /** Offered with `appBlocked` when the caller can move the page (not for mount content). */
  onMoveIntoOwnFolder?: () => Promise<void>
  /** Whether the caller may mint a missing APP default (drives its Retry affordance) —
   *  the caller's share capability; a non-sharer never sees Retry there (it would only 403).
   *  The SOURCE default needs no sharing right — anyone who can open the target may use it. */
  canCreate: boolean
  onCopy: (url: string) => void
  onShowQr: (url: string) => void
  onEnsure: (kind: 'app' | 'quick_view') => Promise<void>
}) {
  const [tab, setTab] = useState<'app' | 'source'>('app')
  const canMake = (t: 'app' | 'source') => t === 'source' || canCreate
  // The set of tabs currently mid-mint — PER TAB so a slow App mint doesn't
  // block the Source mint (they're independent).
  const [ensuring, setEnsuring] = useState<ReadonlySet<'app' | 'source'>>(() => new Set())

  const ensureFor = async (t: 'app' | 'source') => {
    if (ensuring.has(t)) return
    setEnsuring((prev) => new Set(prev).add(t))
    try { await onEnsure(t === 'app' ? 'app' : 'quick_view') }
    finally { setEnsuring((prev) => { const n = new Set(prev); n.delete(t); return n }) }
  }

  // One default's slot: its card, a "Preparing …" spinner while minting, or a
  // Retry box when it's missing and mintable. Absent + not mintable ⇒ nothing.
  const renderSlot = (t: 'app' | 'source', link: ShareLinkWithUrl | null, label: string, badge: 'app' | 'private') => {
    const name = t === 'app' ? 'app' : 'source'
    // An App default whose folder is one of the drive's own: refused at mint (#889), or
    // stored before the ban and no longer served — explain, and offer the way out.
    if (t === 'app' && (appBlocked || link?.appRoot?.blocked)) {
      return <BlockedAppDefaultCard detail={appBlocked?.detail ?? null} folderName={appBlocked?.folder.name ?? link?.appRoot?.name ?? ''} stale={!!link} onMove={onMoveIntoOwnFolder} />
    }
    if (link) return <DefaultLinkCard link={link} label={label} badge={badge} onCopy={() => onCopy(link.url)} onShowQr={() => onShowQr(link.url)} />
    if (ensuring.has(t)) return (
      <div className="rounded-xl border border-indigo-400/30 px-3 py-6 flex items-center justify-center gap-2 text-xs themed-text-muted">
        <Loader2 size={14} className="animate-spin" /> Preparing {name} link…
      </div>
    )
    if (!canMake(t)) return null
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-4 flex items-center justify-center gap-2 text-xs">
        <span className="themed-text-muted">Couldn't prepare the {name} link.</span>
        <button type="button" onClick={() => void ensureFor(t)} className="themed-btn-secondary px-2.5 py-1 rounded-md text-xs font-medium">Retry</button>
      </div>
    )
  }

  // (The Source default is always available to whoever can open the target, so there is always something to render.)

  // Non-app target: a single Source ("Default link") slot, no toggle.
  if (!isAppEligible) return renderSlot('source', sourceLink, 'Private link', 'private')

  // A tab is offered when its default exists OR the caller can mint it. A
  // non-creator viewing only one of the two therefore never sees a dead tab.
  const tabDefs = [
    { value: 'app' as const, label: 'PDApp', accent: '#d38c25', link: appLink, cardLabel: 'Private app link', badge: 'app' as const },
    { value: 'source' as const, label: 'Source Folder', link: sourceLink, cardLabel: 'Private source link', badge: 'private' as const },
  ]
  const visible = tabDefs.filter((t) => t.link || canMake(t.value))
  // Clamp the active tab to an available one (e.g. tab defaulted to 'app' but
  // only Source is offered).
  const activeVal = visible.some((t) => t.value === tab) ? tab : visible[0].value
  const activeDef = tabDefs.find((t) => t.value === activeVal)!

  const switchTab = (t: 'app' | 'source') => {
    setTab(t)
    // Lazily mint the tab's default if it's missing (Source on first switch;
    // App only if its eager mint failed). No-op when present or non-mintable.
    const def = tabDefs.find((d) => d.value === t)
    if (def && !def.link && canMake(t)) void ensureFor(t)
  }

  // Only one default available (non-creator) → render it directly, no toggle.
  if (visible.length <= 1) return renderSlot(activeDef.value, activeDef.link, activeDef.cardLabel, activeDef.badge)

  return (
    <div className="space-y-2">
      <GlassToggle
        value={activeVal}
        onChange={switchTab}
        options={visible.map((t) => ({ value: t.value, label: t.label, accent: t.accent }))}
      />
      {renderSlot(activeDef.value, activeDef.link, activeDef.cardLabel, activeDef.badge)}
    </div>
  )
}

function DefaultLinkCard({ link, label, badge, onCopy, onShowQr }: { link: ShareLinkWithUrl; label: string; badge: 'app' | 'private'; onCopy: () => void; onShowQr: () => void }) {
  const app = badge === 'app'
  // App default reads amber-gold (matches the App pill / badge); the native
  // source/plain default keeps the indigo "3D lift" chrome.
  // A DARK base under the translucent tint (dark/dim themes): the dialog is glass, so whatever is
  // behind it — a white page — showed through and washed out the muted subtext (owner,
  // 2026-10-03). The light theme keeps its plain translucent card.
  const cardBase = 'bg-zinc-950/55 [.theme-light_&]:bg-transparent'
  const cardClass = app
    ? cardBase + ' rounded-xl border border-amber-400/35 bg-gradient-to-b from-amber-500/[0.16] to-amber-500/[0.04] px-3 py-2.5 shadow-[0_8px_20px_-6px_rgba(211,140,37,0.4),inset_0_1px_1px_rgba(255,255,255,0.3)]'
    : cardBase + ' rounded-xl border border-indigo-400/30 bg-gradient-to-b from-[oklab(0.8_0.0252_-0.0968/0.2)] to-[oklab(0.88_0.0724_-0.069/0.05)] [.theme-dim_&]:from-[oklab(0.8_0.03_-0.1/0.2)] [.theme-dim_&]:to-[oklab(0.32_0.02_-0.05)] [.theme-dark_&]:to-[oklab(0.83_-0.02_-0.09/0.19)] px-3 py-2.5 shadow-[0_8px_20px_-6px_rgba(99,102,241,0.4),inset_0_1px_1px_rgba(255,255,255,0.3)]'
  const qrRing = app ? 'hover:ring-amber-400/60' : 'hover:ring-indigo-400/60'
  const copyClass = app
    ? 'border-amber-400/60 hover:border-amber-300 hover:bg-amber-500/15 text-amber-100 [.theme-light_&]:text-amber-700 [.theme-light_&]:border-amber-500/60 [.theme-light_&]:hover:border-amber-500'
    : 'border-indigo-400/60 hover:border-indigo-300 hover:bg-indigo-500/15 text-indigo-100 [.theme-light_&]:text-indigo-700 [.theme-light_&]:border-indigo-500/60 [.theme-light_&]:hover:border-indigo-500'
  return (
    // 3D lift: same-hue alpha fade (not a lighting sheen) + a tinted drop shadow
    // floating the card off the glass + an inset top-light on the upper edge.
    <div className={cardClass}>
      <div className="flex items-center gap-3">
        {/* QR of the native link — click to enlarge in the centered modal. */}
        <button
          type="button"
          onClick={onShowQr}
          className={`shrink-0 rounded-md bg-white p-1.5 shadow-[0_2px_6px_-1px_rgba(30,27,75,0.2)] hover:ring-2 ${qrRing} transition-shadow`}
          title="Show QR code"
          aria-label="Show QR code"
        >
          <QRCodeSVG value={link.url} size={72} level="M" marginSize={0} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            {badge === 'app' ? (
              <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/30 text-amber-200 [.theme-light_&]:text-amber-700 text-[10px] font-semibold uppercase tracking-wide">
                <PDAppIcon size={10} /> App
              </span>
            ) : (
              // No permission icon here, unlike the row chips: the default link
              // is immutable, so it is always Private and always view-level —
              // an icon that can never change carries no information.
              <span className={`inline-flex items-center rounded px-1.5 py-0.5 ${AUDIENCE_CHIP_CLS.private} text-[10px] font-semibold uppercase tracking-wide`}>
                {AUDIENCE_LABEL.private}
              </span>
            )}
            <span className="text-xs font-medium themed-text">{label}</span>
          </div>
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title={link.url}
            className="block mt-1.5 text-[11px] themed-text-secondary hover:themed-text hover:underline truncate font-mono"
          >{link.url}</a>
          <p className="mt-1.5 text-[11px] themed-text-muted leading-relaxed">
            {/* A default carries nobody's view (owner, 2026-10-03): each visitor opens only what they
                already can — so no "shares the whole folder" warning here (that is a custom link's). */}
            {link.appRoot
              ? `Only opens for people who already have access to “${link.appRoot.name}” — each sees only what they can open.`
              : 'Only opens for people who already have access — each sees only what they can open.'}
          </p>
        </div>
        <button
          type="button"
          onClick={onCopy}
          // Engraved BORDER (not a recessed face): two bright 1px lines
          // hugging the ring — inside its top edge and outside its bottom —
          // so the outline reads as carved into the card (edge-lit groove)
          // while the pill face stays flat and the same material.
          className={`shrink-0 inline-flex items-center gap-2 rounded-full border ${copyClass} text-sm font-medium px-5 py-2.5 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.4),0_1px_0_rgba(255,255,255,0.4)]`}
          title="Copy link"
          aria-label="Copy default link"
        >
          <Link2 size={16} /> Copy Link
        </button>
      </div>
    </div>
  )
}

/** In place of the links when the folder is one of the drive's own: it is never shared as a whole. */
function ForbiddenFolderCard({ detail }: { detail: string }) {
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-3" data-testid="forbidden-folder-share">
      <div className="flex gap-2">
        <Lock size={15} className="text-amber-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium themed-text">Forbidden folder</p>
          <p className="mt-1 text-xs themed-text-secondary leading-relaxed">{detail}</p>
        </div>
      </div>
    </div>
  )
}


/**
 * In place of the default app link when the page sits directly in one of the drive's
 * own folders (#889): no link — an app link would serve that whole folder — and the
 * way out. `stale` = a default made before the ban, which no longer opens.
 */
function BlockedAppDefaultCard({ detail, folderName, stale, onMove }: {
  detail: string | null
  folderName: string
  stale: boolean
  onMove?: () => Promise<void>
}) {
  const [moving, setMoving] = useState(false)
  return (
    <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 px-3 py-3" data-testid="default-app-blocked">
      <div className="flex gap-2">
        <Lock size={15} className="text-amber-500 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          {/* The same name the picker and the folder card use for this state (owner wording). */}
          <p className="text-sm font-medium themed-text">Forbidden folder</p>
          {stale && <p className="mt-0.5 text-[11px] themed-text-muted">This app link no longer opens.</p>}
          <p className="mt-1 text-xs themed-text-secondary leading-relaxed">
            {detail || `“${folderName}” is one of the drive's own folders, so an app link can't serve it. Put the HTML file in its own folder and share it from there.`}
          </p>
          {onMove && (
            <>
              <p className="mt-1 text-[11px] themed-text-muted leading-relaxed">
                Only this page moves. Move any files it uses (images, scripts, styles) into the new folder too.
              </p>
              <button
                type="button"
                disabled={moving}
                onClick={async () => { setMoving(true); try { await onMove() } finally { setMoving(false) } }}
                className="mt-2 inline-flex items-center gap-1.5 themed-btn-secondary px-2.5 py-1 rounded-md text-xs font-medium disabled:opacity-60"
                data-testid="move-into-own-folder"
              >
                {moving ? <Loader2 size={12} className="animate-spin" /> : <FolderInput size={12} />} Move into its own folder
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export function LinkRow({
  link,
  isEditing = false,
  isBusy = false,
  isFolder,
  canGrantEdit = false,
  isHighlighted = false,
  readOnly = false,
  creatorShareOverlay = false,
  creatorIsOrg = false,
  creatorOrgName,
  onStartEdit,
  onCancelEdit,
  onCopy,
  onShowQr,
  onRevoke,
  onClose,
  onLinkPatched,
}: {
  link: ShareLinkWithUrl
  isEditing?: boolean
  isBusy?: boolean
  isFolder: boolean
  canGrantEdit?: boolean
  /** Overlay a yellow/black door-open corner badge on the creator avatar —
   *  flags the row as an external reshare of your content (the creator entered
   *  from outside). The avatar itself stays the creator's normal personal one. */
  creatorShareOverlay?: boolean
  /** The reshare creator's home tenant is an organization (vs a personal
   *  account). When true the avatar hover shows the ORG name (creatorOrgName)
   *  instead of the resharer's personal name. Only meaningful when
   *  creatorShareOverlay is true. */
  creatorIsOrg?: boolean
  /** The reshare creator's organization (tenant) display name — shown in place
   *  of the personal name when creatorIsOrg is true. */
  creatorOrgName?: string
  /** Parent flips this true after a successful mint: the row scrolls
   *  itself into view and runs a brief box-shadow flash to draw the eye
   *  to the new entry. Parent clears the flag after the animation. */
  isHighlighted?: boolean
  /** True in audit / read-only contexts (e.g. WhoHasAccess > Share
   *  links). Hides the edit chevron and the avatar-stack edit affordance
   *  so the row is purely informational; the EditPanel doesn't mount. */
  readOnly?: boolean
  onStartEdit?: () => void
  onCancelEdit?: () => void
  onCopy?: () => void
  /** Open the shared QR modal (centered over the dialog) for this link's URL. */
  onShowQr?: (url: string) => void
  onRevoke?: () => void
  /** Close the edit panel (called after a successful Save). */
  onClose?: () => void
  /** Notify the parent that this link row's state changed on the
   *  server (auto-save before Send, or the Save button). The parent
   *  uses it to keep its list cache in sync without re-fetching. */
  onLinkPatched?: (updated: ShareLinkWithUrl) => void
}) {
  // Can this link be extended (your access reaches items its visitors can't)? Asked only for a
  // link you may change (extending is a settings change) — shown on the card's top line so it is
  // visible without opening the card (owner, 2026-10-03). Re-asked when the link changes (an
  // extension patches it).
  const [canExtend, setCanExtend] = useState(false)
  const extendEpoch = extendEpochs.get(link.id) ?? 0
  // What the server's answer depends on besides the id: the link's recipients (its level) and its
  // root (re-scoped by a Save or a move) — a change there re-asks (re-review of review B2).
  const capKey = JSON.stringify([link.recipientGrants, link.rootFileId, link.appRootFileId])
  useEffect(() => {
    // Not gated on `canEdit` (a link returned by a mutation doesn't carry it) — the server answers
    // `wouldExtend: false` to anyone without authority over the link anyway.
    if (readOnly || link.isRevoked) { setCanExtend(false); return }
    let cancelled = false
    previewShareExtend(link.id)
      .then((p) => { if (!cancelled) setCanExtend(!!p.wouldExtend) })
      .catch(() => { if (!cancelled) setCanExtend(false) })
    return () => { cancelled = true }
    // Keyed on the link's id (+ revoked, + an extension through this page) — NOT the object, which
    // every list refetch replaces: one preview walks the link's whole reach (review B2).
  }, [link.id, link.isRevoked, readOnly, extendEpoch, capKey])
  const status = describeStatus(link)
  // For an external reshare, the avatar hover names the OUTSIDER: the org's name
  // when they re-shared from an organization, else the resharer's personal name.
  const outsiderName = creatorShareOverlay
    ? ((creatorIsOrg && creatorOrgName?.trim()) || link.creator?.name?.trim() || link.creator?.email || '')
    : ''
  const rowRef = useRef<HTMLDivElement>(null)
  // Live badge preview. Recipient-grant edits are BUFFERED in the edit
  // panel (nothing persists until Save), so while the panel is open its
  // staged grants drive the prefix chip — unchecking "Anyone with the
  // link" flips the badge to Native immediately instead of after Save.
  const [stagedGrants, setStagedGrants] = useState<RecipientGrants | null>(null)
  useEffect(() => {
    if (!isEditing) setStagedGrants(null)
  }, [isEditing])
  const badgeGrants = (isEditing && stagedGrants) ? stagedGrants : link.recipientGrants
  /**
   * Whether this link's settings can still be changed.
   *
   * `link.canEdit` only reports the caller's permission on the SOURCE FILE, so
   * on its own it still offered the pencil — and a fully live, saveable panel —
   * for a link that had already been revoked. Revoking burns the token
   * one-way, so there is nothing left to configure.
   *
   * The panel itself stays reachable: the recipients, expiry and revoke reason
   * are worth reading after the fact. Only the editing goes.
   */
  const settingsEditable = link.canEdit && !link.isRevoked
  // The card's own recipient chips name people too — the tooltips below — and
  // they are what you see WITHOUT expanding the panel. Same resolution as the
  // expanded list, or the same person reads differently one click apart.
  const memberNames = useMemberNames()
  /**
   * Creator initials from the real display NAME when the API supplied one,
   * falling back to the email.
   *
   * `link.creator` has carried `{ id, name, email }` all along, but the glyph
   * read off the email while the aria-label beside it already said
   * `name || email` — so a screen reader heard "Season Chu" over a disc
   * showing "SE". Same rule as the permission dialog's `memberInitial`, which
   * is what made one person look like two accounts across the two surfaces.
   *
   * Recipient rows deliberately keep the email: their only identity IS the
   * email (the name on hover there is synthesised from it), and a mixed list
   * where org members read differently from outsiders would be less legible,
   * not more.
   */
  const creatorInitials = link.creator
    ? getInitials(link.creator.name?.trim() || link.creator.email)
    : ''
  // Pick the flash color from the prefix chip so the focus animation
  // reads as "that thing you clicked" rather than a generic emerald
  // pulse. Mirrors the chip palette, which is keyed on AUDIENCE (the
  // chip's text) rather than permission (the chip's icon):
  // App→amber, Public→blue, Restricted→purple, Private→indigo.
  const flashColor = useMemo(() => {
    if (link.kind === 'app') {
      return { ring: 'rgb(251, 191, 36)', glow: 'rgba(245, 158, 11, 0.55)', fill: 'rgb(245, 158, 11)' }
    }
    const audience = linkAudience(link.recipientGrants, link.strictAudience)
    if (audience === 'public') {
      return { ring: 'rgb(96, 165, 250)', glow: 'rgba(59, 130, 246, 0.55)', fill: 'rgb(59, 130, 246)' }
    }
    if (audience === 'restricted') {
      return { ring: 'rgb(192, 132, 252)', glow: 'rgba(168, 85, 247, 0.55)', fill: 'rgb(168, 85, 247)' }
    }
    return { ring: 'rgb(129, 140, 248)', glow: 'rgba(99, 102, 241, 0.55)', fill: 'rgb(99, 102, 241)' }
  }, [link])
  // Web Animations API drives the flash so the keyframes paint
  // reliably regardless of React render timing — the earlier
  // state-based approach raced the browser's paint and never showed.
  useEffect(() => {
    if (!isHighlighted || !rowRef.current) return
    const el = rowRef.current
    el.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    // Read the row's real bg + border so the animation lands on them
    // exactly — animating to fixed values (transparent / a guessed
    // grey) and then dropping the inline style snaps back to the
    // themed colors through a flicker.
    const computed = getComputedStyle(el)
    const restingBg = computed.backgroundColor
    const restingBorderColor = computed.borderTopColor
    const anim = el.animate(
      [
        {
          boxShadow: `0 0 0 2px ${flashColor.ring}, 0 10px 28px -6px ${flashColor.glow}`,
          backgroundColor: flashColor.fill,
          borderColor: flashColor.ring,
        },
        {
          boxShadow: '0 0 0 0 rgba(0, 0, 0, 0), 0 0 0 0 rgba(0, 0, 0, 0)',
          backgroundColor: restingBg,
          borderColor: restingBorderColor,
        },
      ],
      { duration: 1500, easing: 'ease-out' },
    )
    return () => anim.cancel()
  }, [isHighlighted, flashColor])
  return (
    <div
      ref={rowRef}
      // ws-drive-pill (DESIGN_DOC §10.4) — the "quiet card on a bright
      // surface" recipe. themed-bg-tertiary blended into the frosted
      // ws-glass-modal; the dpill hairline + lift shadow float the row
      // on the glass in all three themes.
      //
      // Extra pb: the meta line ("N views · expires …") is the last thing in
      // the card and a flat p-3 left it sitting on the bottom hairline.
      className="ws-drive-pill rounded-lg p-3 pb-4 relative overflow-hidden">
      {/* Decorative watermark — oversized link icon spilling out of the
          top-right corner. Faded via themed-text colour + low opacity so
          it reads as background in both light and dark themes. Sits
          below the content (pointer-events-none, content uses `relative`
          to claim a higher position in source order). */}
      <Link2
        size={140}
        aria-hidden="true"
        className="absolute top-3 -right-8 themed-text opacity-[0.06] pointer-events-none rotate-[132deg] select-none"
      />
      <div className="min-w-0 relative">
        <div className="flex items-center gap-2 mb-1">
          <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
            <Tooltip content={status.label} always placement="top" className="inline-flex items-center">
              <code className="text-xs leading-none font-mono themed-text rounded inline-flex items-center overflow-hidden h-5">
                {(() => {
                  // Prefix chip = PERMISSION icon + AUDIENCE text. The two are
                  // independent questions ("what can they do" vs "who are they"),
                  // and folding them into one slot lost the audience on every
                  // public link — a `*` link with a per-recipient override badged
                  // "View" and never said it was public.
                  //
                  // Icon mirrors the PermissionPicker buckets:
                  //   - Edit          — SquarePen
                  //   - View (Live)   — Eye + small green dot
                  //   - View (Saved)  — Eye
                  //   - App           — the PDApp mark (app links are view-capped, Q5/Q8)
                  // Text + colour come from linkAudience, so this chip, the
                  // General-access label and both filters can't disagree.
                  const audience = linkAudience(badgeGrants, link.strictAudience)
                  const primaryPerm = linkPrimaryPermission(badgeGrants)
                  const primaryLive = linkPrimaryLiveMode(badgeGrants)
                  const isApp = link.kind === 'app'
                  const icon: ReactNode = isApp
                    ? <PDAppIcon size={10} />
                    : primaryPerm === 'edit'
                      ? <SquarePen size={10} />
                      : primaryLive
                        ? (
                          <span className="relative inline-flex items-center justify-center" style={{ width: 10, height: 10 }}>
                            <Eye size={10} />
                            <span
                              className="absolute -top-0.5 -right-0.5 rounded-full bg-emerald-400"
                              style={{ width: 4, height: 4 }}
                              aria-hidden="true"
                            />
                          </span>
                        )
                        : <Eye size={10} />
                  // App keeps its own label + amber: "this is a running app" is a
                  // stronger thing to say about the link than who may open it, and
                  // the Lock/Home chip after the token carries the audience there.
                  const label = isApp ? 'App' : AUDIENCE_LABEL[audience]
                  let cls = isApp
                    ? 'bg-amber-500/30 text-amber-200 [.theme-light_&]:text-amber-700'
                    : AUDIENCE_CHIP_CLS[audience]
                  if (status.label !== 'Active') {
                    cls = 'bg-red-500/30 text-red-200 [.theme-light_&]:text-red-700'
                  }
                  return (
                    <span className={`inline-flex items-center gap-1 h-full px-1.5 font-sans font-medium ${cls}`}>
                      {icon}
                      <span>{label}</span>
                    </span>
                  )
                })()}
                <span className="px-1.5 h-full ws-glass-chip inline-flex items-center gap-1.5">
                  <span className={status.label === 'Active' ? '' : 'line-through opacity-70'}>
                    {link.displayToken ?? link.token}
                  </span>
                  <span
                    className={`inline-block w-1.5 h-1.5 rounded-full ${status.label === 'Active' ? 'bg-emerald-500' : 'bg-red-500'}`}
                    aria-label={status.label}
                  />
                </span>
                {link.kind === 'app' && (() => {
                  // Audience glyph — App ONLY. Every other kind now says its
                  // audience in words on the prefix chip, so repeating it here
                  // as an icon was pure noise; the App chip is the one that
                  // spends its text on the kind instead, leaving it with no
                  // audience signal at all. Public needs no glyph (a link that
                  // opens for everyone isn't gated), so this renders for the two
                  // gated audiences only.
                  const audience = linkAudience(link.recipientGrants, link.strictAudience)
                  if (audience === 'public') return null
                  return (
                    <span
                      className={`inline-flex items-center h-full px-1.5 ${audience === 'restricted'
                        ? 'bg-purple-500/30 text-purple-200 [.theme-light_&]:bg-purple-500/25 [.theme-light_&]:text-purple-700'
                        : 'bg-indigo-500/30 text-indigo-200 [.theme-light_&]:text-indigo-700'}`}
                      title={AUDIENCE_HINT[audience]}
                    >
                      {audience === 'restricted'
                        ? <Lock size={10} aria-label={AUDIENCE_LABEL.restricted} />
                        : <Home size={10} aria-label={AUDIENCE_LABEL.private} />}
                    </span>
                  )
                })()}
              </code>
            </Tooltip>
            {/* QR toggle — sits beside the link ID; opens the centered QR modal. */}
            <button
              type="button"
              onClick={() => onShowQr?.(link.url)}
              className="p-1 themed-text-muted hover:themed-text rounded hover:themed-bg-hover shrink-0"
              title="Show QR code"
              aria-label="Show QR code"
            >
              <QrCode size={16} />
            </button>
            {link.appRoot?.blocked && !link.isRevoked && (
              // Made before #889 on one of the drive's own folders — it serves nothing
              // now. Whoever may move its folder picks another in the edit panel.
              <span
                className="shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/20 text-amber-300 [.theme-light_&]:text-amber-800 text-[10px] font-semibold"
                title={`“${link.appRoot.name}” is one of the drive's own folders, so this link no longer opens. Choose another app folder.`}
                data-testid="link-needs-app-folder"
              >
                <AlertTriangle size={10} /> Needs a new app folder
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {/* The default native link is immutable — no edit/settings panel (and
                thus no revoke, which lives in that panel's footer). Copy only. */}
            {!readOnly && !link.isDefault && (
            <button
              type="button"
              onClick={isEditing ? onCancelEdit : onStartEdit}
              disabled={isBusy}
              className="p-1.5 themed-text-muted hover:themed-text rounded themed-bg-hover disabled:opacity-50"
              title={
                isEditing
                  ? 'Collapse panel'
                  : settingsEditable
                    ? 'Edit settings'
                    : 'View details'
              }
              aria-label={
                isEditing
                  ? 'Collapse panel'
                  : settingsEditable
                    ? 'Edit settings'
                    : 'View details'
              }
            >
              {isEditing
                ? <X size={14} />
                : settingsEditable
                  ? <Pencil size={14} />
                  : <ChevronDown size={14} />}
            </button>
            )}
            {/* (0084) Creator avatar replaces the top-right Revoke
                button — Revoke moves into the EditPanel footer where
                destructive actions are conventionally grouped with
                the Cancel / Save row. Personal-tenant lists have
                `creator: null` so the slot stays empty. */}
            {link.creator && !link.isDefault && (
              <Tooltip
                always
                placement="top"
                content={
                  <div className="text-left">
                    <div className="text-[10px] uppercase tracking-wide opacity-70">Created by</div>
                    <div className="font-medium">
                      {creatorShareOverlay ? outsiderName : (link.creator.name?.trim() || link.creator.email)}
                      {creatorShareOverlay && (
                        <span className="ml-1 rounded px-1 bg-amber-400/25 text-amber-200 text-[10px] font-medium">
                          outsider
                        </span>
                      )}
                    </div>
                    <div className="text-[10px] opacity-80">{link.creator.email}</div>
                  </div>
                }
              >
                <div className="relative inline-flex shrink-0">
                  {creatorShareOverlay ? (
                    // External resharer — an "eclipse" avatar: the creator's KO
                    // initials under a 30% shadow at rest, crossfading to an amber
                    // door-open glyph under a 50% shadow on hover. Flags them as
                    // someone who entered from outside without a separate badge;
                    // identity is in the tooltip ("Kok (personal)").
                    <div
                      className="group/eclipse relative w-5 h-5 rounded-full overflow-hidden select-none cursor-default bg-black"
                      aria-label={`Created by ${outsiderName} outsider`}
                    >
                      {/* KO avatar disc — same-size rounded circle over a black base.
                          At rest it's shifted right, so its rounded LEFT edge is the
                          "(" eclipse terminator and black shows on the left; on hover
                          it slides back to centre, overflowing left to cover the black
                          and fill the circle. KO rides along with it. */}
                      <div className={`absolute inset-0 rounded-full ${avatarBg(link.creator.email)} flex items-center justify-center text-white text-[8px] font-semibold translate-x-[7px] group-hover/eclipse:translate-x-0 transition-transform duration-200`}>
                        {creatorInitials}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={`w-5 h-5 rounded-full ${avatarBg(link.creator.email)} flex items-center justify-center text-white text-[8px] font-semibold select-none`}
                      aria-label={`Created by ${link.creator.name?.trim() || link.creator.email}`}
                    >
                      {creatorInitials}
                    </div>
                  )}
                </div>
              </Tooltip>
            )}
            {/* Default inbound reshare — the .shared copy's OWN default link (from
                another tenant). isDefault → no edit button, and no creator to hang an
                avatar on, so this slot would be empty. A muted label fills it.
                Non-default reshares keep their creator avatar (hover names the
                resharer), so they get no label. */}
            {creatorShareOverlay && link.isDefault && (
              <span
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-indigo-500 text-white whitespace-nowrap shrink-0"
                title="Default link on the shared copy — read-only"
              >
                <Link2 size={11} className="shrink-0" />
                Shared default link
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs themed-text-secondary hover:themed-text hover:underline truncate min-w-0"
            title={link.url}
          >
            {link.url}
          </a>
          <button
            type="button"
            onClick={onCopy}
            className="p-1 themed-text-muted hover:themed-text rounded themed-bg-hover shrink-0"
            title="Copy link"
            aria-label="Copy link"
          >
            <Copy size={12} />
          </button>
        </div>
        {/* Recipient avatars. First N rendered as colored initial pills;
            the rest collapse into a "+N" pill that tooltips the full
            overflow list on hover. Mirrors the People with access avatar
            style so the row chip + edit panel stay visually consistent. */}
        {(() => {
          const emails = Object.keys(link.recipientGrants).filter((k) => k !== '*').sort()
          if (emails.length === 0) return null
          const MAX_AVATARS = 5
          const visible = emails.slice(0, MAX_AVATARS)
          const overflowList = emails.slice(MAX_AVATARS)
          // Stacked avatar group — negative horizontal margin overlaps
          // each circle slightly, and a card-bg-colored ring cuts a
          // visual gap between them so adjacent pills don't blur into
          // each other. The card surface is ws-drive-pill, whose bg is a
          // faint translucent tint — invisible as a 1.5px ring on dark/dim —
          // so the ring uses --ws-dpill-avatar-ring, an OPAQUE per-theme
          // approximation of the pill's effective surface.
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (readOnly) return
                if (!isEditing) onStartEdit?.()
              }}
              className={`flex items-center mt-1.5 rounded-full ${readOnly ? '' : 'cursor-pointer hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500/60'}`}
              title={readOnly ? undefined : 'Edit recipients'}
              aria-label={readOnly ? 'Recipients' : 'Edit recipients'}
            >
              {visible.map((email, i) => (
                <Tooltip
                  key={email}
                  always
                  placement="top"
                  content={
                    <div className="text-left">
                      <div className="font-medium">{recipientDisplayName(email, memberNames)}</div>
                      <div className="text-[10px] opacity-80">{email}</div>
                    </div>
                  }
                >
                  <div
                    className={`w-5 h-5 rounded-full ${avatarBg(email)} flex items-center justify-center text-white text-[8px] font-semibold shrink-0 select-none relative`}
                    style={{
                      marginLeft: i === 0 ? 0 : -5,
                      boxShadow: '0 0 0 1.5px var(--ws-dpill-avatar-ring)',
                      zIndex: visible.length - i,
                    }}
                  >
                    {getInitials(recipientDisplayName(email, memberNames))}
                  </div>
                </Tooltip>
              ))}
              {overflowList.length > 0 && (() => {
                const TOOLTIP_PREVIEW = 3
                const previewed = overflowList.slice(0, TOOLTIP_PREVIEW)
                const remainder = overflowList.length - previewed.length
                return (
                  <Tooltip
                    always
                    placement="top"
                    content={
                      <div className="text-left flex flex-col gap-1.5">
                        {previewed.map((email) => (
                          <div key={email}>
                            <div className="font-medium">{recipientDisplayName(email, memberNames)}</div>
                            <div className="text-[10px] opacity-80">{email}</div>
                          </div>
                        ))}
                        {remainder > 0 && (
                          <div className="text-[10px] opacity-80">…{remainder} more</div>
                        )}
                      </div>
                    }
                  >
                    <div
                      className="w-5 h-5 rounded-full themed-bg-secondary themed-text flex items-center justify-center text-[9px] font-semibold shrink-0 select-none"
                      style={{
                        marginLeft: -5,
                        boxShadow: '0 0 0 1.5px var(--ws-dpill-avatar-ring)',
                      }}
                    >
                      +{overflowList.length}
                    </div>
                  </Tooltip>
                )
              })()}
            </button>
          )
        })()}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] themed-text-muted">
          {/* (0084) Creator attribution lives on the avatar's tooltip
              above — the explicit "Created by …" line was redundant. */}
          <span>
            {link.viewCount} {link.viewCount === 1 ? 'view' : 'views'}
          </span>
          {canExtend && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); if (!isEditing) onStartEdit?.() }}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 bg-amber-500/20 text-amber-400 [.theme-light_&]:text-amber-700 font-medium hover:bg-amber-500/30"
              title="Your access reaches items this link's visitors can't see — open it to extend"
              data-testid="link-can-extend"
            >
              <AlertTriangle size={10} /> Can be extended
            </button>
          )}
          {link.expiresAt && (
            <span>· expires {formatRelative(link.expiresAt)}</span>
          )}
          {link.lastViewedAt && (
            <span>· last viewed {formatRelative(link.lastViewedAt)}</span>
          )}
        </div>
      </div>
      {/* Break out of the LinkRow's p-3 so the collapsing edit panel
          spans the card's full width (touches the inner edges of the
          card's border). The ws-dpill-sunken fill in EditPanel reads
          as a clear separator from the chip header. Read-only callers
          (e.g. WhoHasAccess audit view) skip the EditPanel entirely. */}
      {!readOnly && (
        <CollapsibleEdit isOpen={isEditing} className={`relative ${isEditing ? '-mx-3 -mb-3 mt-3' : '-mx-3 -mb-3'}`}>
          <EditPanel
            link={link}
            isBusy={isBusy}
            readOnly={link.isRevoked}
            isFolder={isFolder}
            canGrantEdit={canGrantEdit}
            isReshare={creatorShareOverlay}
            onCancel={onCancelEdit ?? (() => {})}
            onClose={onClose ?? (() => {})}
            onLinkPatched={onLinkPatched ?? (() => {})}
            onRevoke={onRevoke ?? (() => {})}
            onStagedGrantsChange={setStagedGrants}
          />
        </CollapsibleEdit>
      )}
    </div>
  )
}

/**
 * Collapsible wrapper for EditPanel. CSS grid-template-rows: 0fr ↔ 1fr is
 * the modern way to animate `height: auto` smoothly without a JS measure
 * loop. The child stays mounted through the close transition (so the
 * panel doesn't snap-disappear); after the transition ends we unmount so
 * the next open starts fresh (mirrors the prior "remount on click" UX
 * where local state initializes from the current link.recipientGrants).
 */
function CollapsibleEdit({
  isOpen,
  className = '',
  children,
  allowOverflowWhenOpen = false,
  instantClose = false,
}: {
  isOpen: boolean
  className?: string
  children: ReactNode
  /** When true, the inner wrapper flips from overflow-hidden to
   *  overflow-visible after the open transition finishes. Required for
   *  children that render absolutely-positioned popovers (e.g.
   *  AppRootPicker's dropdown) that would otherwise be clipped. */
  allowOverflowWhenOpen?: boolean
  /** When true and `isOpen` flips to false, skip the grid-rows
   *  transition and unmount immediately. Used for the share-link create
   *  panel so it doesn't linger after a successful mint. */
  instantClose?: boolean
}) {
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [overflowVisible, setOverflowVisible] = useState(isOpen && allowOverflowWhenOpen)

  useEffect(() => {
    if (isOpen) setShouldRender(true)
    if (!isOpen) {
      setOverflowVisible(false)
      // Bypass the grid-rows transition — `transitionend` won't fire so
      // we have to drop the mount ourselves.
      if (instantClose) setShouldRender(false)
    }
  }, [isOpen, instantClose])

  const animating = !(instantClose && !isOpen)

  return (
    <div
      className={`grid ${animating ? 'transition-[grid-template-rows] duration-300 ease-in-out' : ''} ${
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      } ${className}`}
      onTransitionEnd={(e) => {
        if (e.propertyName !== 'grid-template-rows') return
        if (!isOpen) setShouldRender(false)
        else if (allowOverflowWhenOpen) setOverflowVisible(true)
      }}
    >
      <div className={`min-h-0 ${overflowVisible ? 'overflow-visible' : 'overflow-hidden'}`}>
        {shouldRender && children}
      </div>
    </div>
  )
}

// ─── Inline edit panel ───────────────────────────────────────────────

function EditPanel({
  link,
  isBusy,
  readOnly = false,
  isFolder,
  canGrantEdit,
  isReshare = false,
  onCancel,
  onClose,
  onLinkPatched,
  onRevoke,
  onStagedGrantsChange,
}: {
  link: ShareLinkWithUrl
  isBusy: boolean
  /** Show the settings but let nothing be changed — a revoked link, whose
   *  token is burned one-way, so there is nothing left to configure. The
   *  panel is still worth opening to read who it was for and why it ended. */
  readOnly?: boolean
  isFolder: boolean
  canGrantEdit: boolean
  /** This row is an external reshare of the caller's own content (caller is the
   *  content owner/admin — the Add-to-Drive APPROVER — even though the link was
   *  created by an outsider). Surfaces the approval section (both toggles). */
  isReshare?: boolean
  /** User dismissed without saving — discard expiry edits. */
  onCancel: () => void
  /** Close the panel after a successful save. */
  onClose: () => void
  /** Bubble up the patched row so the parent's list cache stays current
   *  without a full re-fetch. Fired by both the explicit Save button
   *  and the auto-save-before-Send flow. */
  onLinkPatched: (updated: ShareLinkWithUrl) => void
  /** Permanently revoke the link. Confirmation + HTTP is handled by the
   *  parent; this hook just fires when the destructive button is clicked. */
  onRevoke: () => void
  /** Mirror of the BUFFERED (not yet saved) recipient grants, fired on
   *  every staged change. LinkRow uses it to live-preview the prefix
   *  chip (View/Native/Restricted) while the panel is open. */
  onStagedGrantsChange?: (grants: RecipientGrants) => void
}) {
  const toast = useToast()
  // `datetime-local` wants YYYY-MM-DDTHH:mm with no timezone — convert
  // from the stored ISO string (which has Z suffix).
  // #897 — the editable settings are drafts over the link (kept across a
  // phone/desktop layout swap, keyed by the link); the saved* copies below stay
  // local, re-derived from the link on every mount.
  const editKey = `shareLink:${link.id}:edit`
  // The fields start from the link AS OPENED and stay there (as before #897),
  // like the saved* baselines they are compared with: a field that followed the
  // live `link` while untouched read an outside change (a list reload, Extend
  // access, another tab) as the user's, and Save sent it.
  const [openedLink] = useState(link)
  const [expiresLocal, setExpiresLocal] = useDraftState<string>(`${editKey}:expires`, () => isoToLocalInput(openedLink.expiresAt))
  // Snapshot the row state at mount time. Save / Cancel both compare
  // against this baseline; auto-save flushes whatever differs from it.
  const [savedExpiresIso, setSavedExpiresIso] = useState<string | null>(link.expiresAt ?? null)
  const [savedGrants, setSavedGrants] = useState<RecipientGrants>(() => ({ ...link.recipientGrants }))
  const [savedAppRootFileId, setSavedAppRootFileId] = useState<string | null>(link.appRootFileId ?? null)
  // (0108) Per-permission Add-to-Drive approval bypass. true = that permission's
  // named recipients auto-establish (no owner confirm).
  const [autoApproveView, setAutoApproveView] = useDraftState<boolean>(`${editKey}:autoView`, openedLink.autoApproveView)
  const [autoApproveEdit, setAutoApproveEdit] = useDraftState<boolean>(`${editKey}:autoEdit`, openedLink.autoApproveEdit)
  const [savedAutoApproveView, setSavedAutoApproveView] = useState<boolean>(link.autoApproveView)
  const [savedAutoApproveEdit, setSavedAutoApproveEdit] = useState<boolean>(link.autoApproveEdit)
  // (0119) Hard off-switch for recipient-initiated Add-to-Drive.
  const [forbidAddToDrive, setForbidAddToDrive] = useDraftState<boolean>(`${editKey}:forbid`, openedLink.forbidAddToDrive)
  const [savedForbidAddToDrive, setSavedForbidAddToDrive] = useState<boolean>(link.forbidAddToDrive)
  const [saving, setSaving] = useState(false)

  // Buffered recipient_grants. Per-row picker writes here; nothing
  // hits the wire until Save (or the auto-save before a per-row Send).
  const [recipientGrants, setRecipientGrants] = useDraftState<RecipientGrants>(
    `${editKey}:grants`, openedLink.recipientGrants,
    // "Unchanged" as `grantsDirty` reads it (any key order): a recipient
    // removed and re-added is no hidden draft (#897). Picker clicks, not
    // keystrokes — a looser rule can't eat typing here.
    { isSame: grantsEqual },
  )
  // Keep LinkRow's badge preview in sync with the buffer (see the prop doc).
  useEffect(() => {
    onStagedGrantsChange?.(recipientGrants)
  }, [recipientGrants, onStagedGrantsChange])
  // (0084) Buffered App root selection. Only relevant for kind='app';
  // for quick_view shares it stays at null and never goes dirty.
  const [appRootFileId, setAppRootFileId] = useDraftState<string | null>(`${editKey}:appRoot`, openedLink.appRootFileId ?? null)

  // (0085) "Your access extends this share" — fetched once on mount.
  // Conditional on the server saying the current caller's reach would
  // cover entities the share doesn't currently expose. Null = not yet
  // fetched, false-ish = no gap, true-ish = show the affordance.
  const [extendPreview, setExtendPreview] = useState<{ wouldExtend: boolean; newlyCoveredEntityCount: number } | null>(null)
  const [extending, setExtending] = useState(false)
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const preview = await previewShareExtend(link.id)
        if (!cancelled) setExtendPreview(preview)
      } catch {
        if (!cancelled) setExtendPreview({ wouldExtend: false, newlyCoveredEntityCount: 0 })
      }
    })()
    return () => { cancelled = true }
  }, [link.id])

  const handleExtendAccess = useCallback(async () => {
    setExtending(true)
    try {
      const res = await extendShareAccess(link.id)
      bumpExtendEpoch(link.id)
      if (res.shareLink) onLinkPatched(res.shareLink)
      setExtendPreview({ wouldExtend: false, newlyCoveredEntityCount: 0 })
      toast.success(
        res.newlyCoveredEntityCount > 0
          ? `Extended access — ${res.newlyCoveredEntityCount} ${res.newlyCoveredEntityCount === 1 ? 'item' : 'items'} now visible to recipients`
          : 'No new items to extend',
      )
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to extend access')
    } finally {
      setExtending(false)
    }
  }, [link.id, onLinkPatched, toast])

  const currentExpiryIso = useMemo(() => localInputToIso(expiresLocal), [expiresLocal])
  const expiryDirty = currentExpiryIso !== savedExpiresIso
  const grantsDirty = useMemo(() => !grantsEqual(recipientGrants, savedGrants), [recipientGrants, savedGrants])
  const appRootDirty = appRootFileId !== savedAppRootFileId
  const approvalDirty = autoApproveView !== savedAutoApproveView || autoApproveEdit !== savedAutoApproveEdit || forbidAddToDrive !== savedForbidAddToDrive
  const dirty = expiryDirty || grantsDirty || appRootDirty || approvalDirty

  // Pure local update — no HTTP fires here. handleSetGrants becomes a
  // straight setState that the picker callsites already use.
  const handleSetGrants = useCallback(
    (next: RecipientGrants | ((prev: RecipientGrants) => RecipientGrants)) => {
      setRecipientGrants(next)
    },
    [],
  )

  // Persist every buffered change in one round. Returns true on
  // success / no-op; false on any failure. Used by the explicit Save
  // button and the Email-Quick-Link auto-save before send.
  const persistChanges = useCallback(async (): Promise<boolean> => {
    if (!dirty) return true
    setSaving(true)
    // #1316 review round 3: the Save the person pressed finishes as the identity that pressed it — the remove / add
    // recipient loop and the settings PATCH go as it, never as the next session; nothing is shown once it ended.
    const batch = pinnedBatch()
    try {
      // Diff against savedGrants and apply via the granular PATCH
      // endpoints (upsert/remove). Each PATCH runs inside the repo's
      // SELECT-FOR-UPDATE transaction on share_links and bumps
      // configVersion, so concurrent edits across two tabs serialize
      // and compose (vs the old bulk PUT which was last-writer-wins
      // and clobbered the other tab's intent silently — round-9 #8).
      // Cost is N round-trips for N changed keys; the dialog edits
      // touch a handful at a time, so the latency is bounded.
      let latest = link
      if (grantsDirty) {
        const savedKeys = new Set(Object.keys(savedGrants))
        const newKeys = new Set(Object.keys(recipientGrants))
        // Removes first — drops any block entries the user just deleted
        // so the inverse-order upserts can't briefly re-grant.
        for (const k of savedKeys) {
          if (!newKeys.has(k)) {
            latest = await batch.run(() => removeRecipient(link.id, k))
          }
        }
        // Upserts: any new key OR any key whose grant value changed.
        for (const k of newKeys) {
          const next = recipientGrants[k]!
          const prev = savedGrants[k]
          if (!prev || prev.permission !== next.permission || prev.liveMode !== next.liveMode) {
            latest = await batch.run(() => upsertRecipient(link.id, k, next))
          }
        }
        setSavedGrants({ ...recipientGrants })
      }
      // (0084) Bundle expiry + appRootFileId into one PATCH /:id — the
      // server applies both, bumps config_version when appRoot changes,
      // and returns the updated row. Either field absent from the patch
      // body is a no-op server-side.
      if (expiryDirty || appRootDirty || approvalDirty) {
        const patch: { expiresAt?: string | null; appRootFileId?: string; autoApproveView?: boolean; autoApproveEdit?: boolean; forbidAddToDrive?: boolean } = {}
        if (expiryDirty) patch.expiresAt = currentExpiryIso
        if (appRootDirty && appRootFileId) patch.appRootFileId = appRootFileId
        if (autoApproveView !== savedAutoApproveView) patch.autoApproveView = autoApproveView
        if (autoApproveEdit !== savedAutoApproveEdit) patch.autoApproveEdit = autoApproveEdit
        if (forbidAddToDrive !== savedForbidAddToDrive) patch.forbidAddToDrive = forbidAddToDrive
        latest = await batch.run(() => updateShareLink(link.id, patch))
        if (expiryDirty) setSavedExpiresIso(currentExpiryIso)
        if (appRootDirty) setSavedAppRootFileId(appRootFileId)
        if (approvalDirty) { setSavedAutoApproveView(autoApproveView); setSavedAutoApproveEdit(autoApproveEdit); setSavedForbidAddToDrive(forbidAddToDrive) }
      }
      if (batch.ended()) return false
      onLinkPatched(latest)
      return true
    } catch (err) {
      if (batch.ended()) return false
      toast.error(err instanceof Error ? ((err as { detail?: string }).detail ?? err.message) : 'Failed to save')
      return false
    } finally {
      setSaving(false)
    }
  }, [dirty, grantsDirty, expiryDirty, appRootDirty, approvalDirty, recipientGrants, savedGrants, currentExpiryIso, appRootFileId, autoApproveView, autoApproveEdit, savedAutoApproveView, savedAutoApproveEdit, forbidAddToDrive, savedForbidAddToDrive, link, onLinkPatched, toast])

  const submit = useCallback(async () => {
    const ok = await persistChanges()
    if (ok) {
      toast.success('Saved')
      onClose()
    }
  }, [persistChanges, onClose, toast])

  return (
    <div className="ws-dpill-sunken">
      {readOnly && (
        <p className="mx-3 mt-3 px-3 py-2 rounded-md text-[11px] themed-text-muted themed-bg-hover">
          This link was revoked — its settings are shown for reference and can no longer be changed.
        </p>
      )}
      {/* `inert` rather than a `disabled` on every control: the settings body
          is shared with the create form and has no read-only mode of its own,
          and inert takes the whole subtree out of reach for pointer AND
          keyboard, which a `pointer-events-none` class would not. */}
      <div inert={readOnly ? true : undefined} className={readOnly ? 'opacity-60' : undefined}>
      {/* (0085) Extend-access affordance — appears when the current
          caller's reach covers entities the share's grantors collectively
          can't see. Preview lists what would become visible; one button
          contributes the caller's access. */}
      {extendPreview?.wouldExtend && (
        // PADDING, not the card's margin: a child's top margin collapsed through this panel and
        // showed up above it instead of as room inside it (owner, 2026-10-03).
        <div className="pt-4">
          <ExtendAccessCard
            linkId={link.id}
            newlyCoveredEntityCount={extendPreview.newlyCoveredEntityCount}
            extending={extending}
            onExtend={() => void handleExtendAccess()}
          />
        </div>
      )}
      <ShareLinkOptionsBody
        mode="edit"
        idPrefix={`edit-${link.id}`}
        linkId={link.id}
        strictAudience={link.strictAudience ?? false}
        recipientGrants={recipientGrants}
        setRecipientGrants={handleSetGrants}
        isAppShare={link.kind === 'app'}
        isFolder={isFolder}
        canGrantEdit={canGrantEdit}
        isReshare={isReshare}
        expiresLocal={expiresLocal}
        setExpiresLocal={setExpiresLocal}
        autoApproveView={autoApproveView}
        setAutoApproveView={setAutoApproveView}
        autoApproveEdit={autoApproveEdit}
        setAutoApproveEdit={setAutoApproveEdit}
        forbidAddToDrive={forbidAddToDrive}
        setForbidAddToDrive={setForbidAddToDrive}
        // (0084) Edit-mode AppRootPicker plumbing — the picker needs
        // fileId to fetch ancestor options, so we use link.rootFileId
        // (the HTML file being shared). Quick-view shares pass through
        // unaffected because ShareLinkOptionsBody guards on isAppShare.
        appRootFileId={appRootFileId}
        // Only the link's creator or the owner may move its folder (the server refuses others).
        setAppRootFileId={link.canRepoint === false ? undefined : setAppRootFileId}
        appRootFileIdDirty={appRootDirty}
        fileId={link.rootFileId}
        // Auto-save buffered changes (recipients + expiry + appRoot)
        // before any per-row Send fires. The recipient should get the
        // link with the current configuration the owner just chose.
        ensureSaved={persistChanges}
        onAddRecipient={async (email, grant) => {
          // Edit mode: persist the new recipient via the granular
          // upsert PATCH — runs inside the server's SELECT-FOR-UPDATE
          // transaction so concurrent tabs serialize correctly (vs
          // the prior bulk PUT which last-writer-wins, round-9 #8).
          // The pre-modal ensureSaved already flushed any pending
          // expiry / app-root edits, so the recipient gets the link
          // with up-to-date config. The quick-link email (when the
          // checkbox is on) is fired by RecipientListBlock via its own
          // `send()` — so the per-row status + dev magic-link callout
          // populate on the freshly-added row — NOT here.
          const key = email.toLowerCase()
          const updated = await upsertRecipient(link.id, key, grant)
          // Reflect the STORED grant, not the one we sent — the server may
          // normalize it (e.g. an app link clamps Edit to View).
          const stored = updated.recipientGrants[key] ?? grant
          const nextRecipients: RecipientGrants = { ...recipientGrants, [key]: stored }
          setRecipientGrants(nextRecipients)
          setSavedGrants(nextRecipients)
          onLinkPatched(updated)
        }}
      />

      </div>

      {/* No own fill — nested inside the ws-dpill-sunken wrapper; stacking a
          second translucent layer would double-darken.
          Outside the inert wrapper: Close must stay reachable, and it is the
          only way to collapse the panel once the header toggle has turned into
          a plain expander. */}
      <div className="flex items-center justify-between gap-2 p-3">
        {/* Destructive action on the left — conventional layout that
            separates it from the affirmative Cancel/Save pair. Revoke
            burns the token permanently; the parent handles confirmation.
            Already revoked → nothing to revoke, and nothing to save. */}
        {!readOnly && (
          <button
            type="button"
            onClick={onRevoke}
            disabled={isBusy || saving}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-red-400 hover:text-red-300 rounded-md hover:bg-red-500/10 disabled:opacity-50"
            title="Revoke (one-way; token is permanently burned)"
          >
            <Ban size={12} />
            Revoke link
          </button>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <button
            type="button"
            onClick={onCancel}
            disabled={isBusy || saving}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium themed-text rounded-md themed-bg-hover disabled:opacity-50"
          >
            {readOnly ? 'Close' : 'Cancel'}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={submit}
              disabled={isBusy || saving || !dirty}
              className="themed-btn-primary inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-md disabled:opacity-50"
            >
              {(isBusy || saving) && <Loader2 size={12} className="animate-spin" />}
              Save
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

/** The App-share exposure warning — every file under the serve root is
 *  fetchable by viewers except `.`/`_` prefixed names. Shown for BOTH app
 *  share shapes: loose HTML (under the App-folder picker) and `.miniapp`
 *  folders (no picker — the folder is its own root). */
function AppExposureWarning({ remintNote = false }: {
  /** Append "sessions are invalidated" — only true where the warning appears
   *  because the App ROOT is being CHANGED (loose-HTML edit mode), not for the
   *  static folder/create variants where nothing invalidates. */
  remintNote?: boolean
}) {
  return (
    <div className="flex gap-2 mt-2 px-2 py-2 rounded border border-amber-500/40 bg-amber-500/5">
      <AlertTriangle size={14} className="text-amber-500 shrink-0 mt-0.5" />
      {/* Light theme needs a DARK amber — the default amber-300 tint
          disappears against the pale panel. */}
      <p className="text-xs text-amber-300/90 [.theme-light_&]:text-amber-800 leading-relaxed">
        <span className="font-semibold">CRITICAL:</span> Every file and subfolder here is fetchable by viewers — except <span className="font-mono">.</span>&thinsp;and <span className="font-mono">_</span>&thinsp;prefixed names, which are never served: <span className="font-mono">_scripts/</span> for backend code your app runs, <span className="font-mono">.secrets/</span> for fully private files.
        {remintNote && ' Existing recipient sessions are invalidated and must re-mint.'}
      </p>
    </div>
  )
}

/**
 * Shared body for both Create and Edit share-link panels.
 *
 * mode='create' — Permission is an editable dropdown (View saved / View
 *   live / Edit). Caller passes setPermission and a controlled
 *   permission value.
 *
 * mode='edit' — Permission is rendered as a read-only badge with copy
 *   explaining it's immutable post-mint (changing view→edit retroactively
 *   would surprise prior viewers; mint a new link instead). The live
 *   toggle still surfaces for view-permission shares so owners can flip
 *   snapshot ↔ live without re-minting.
 *
 * Keeping both panels on this surface means the form rendering only
 * lives once — adding a new field or tweaking visual rhythm flows to
 * both contexts automatically. State is hoisted to the callers so each
 * panel owns its own validation / submit semantics (Create's "all-or-
 * nothing mint" vs Edit's "diff-against-existing patch").
 */
function ShareLinkOptionsBody({
  mode,
  idPrefix,
  recipientGrants,
  setRecipientGrants,
  strictAudience = true,
  isAppShare = false,
  isFolder = false,
  canGrantEdit = false,
  isReshare = false,
  expiresLocal,
  setExpiresLocal,
  autoApproveView,
  setAutoApproveView,
  autoApproveEdit,
  setAutoApproveEdit,
  forbidAddToDrive,
  setForbidAddToDrive,
  appRootFileId,
  setAppRootFileId,
  appRootFileIdDirty,
  fileId,
  linkId = null,
  ensureSaved,
  onAddRecipient,
  notifyEmails,
  setNotifyEmail,
}: {
  mode: 'create' | 'edit'
  idPrefix: string
  /** Decision 10 (+0083): per-recipient grant map. Single source of
   *  truth for who has access, at what level, and with what update
   *  visibility. The General access section shows the "*" wildcard
   *  entry; the People list shows email entries. Each row has its own
   *  permission dropdown that writes both permission AND liveMode. */
  recipientGrants: RecipientGrants
  /** 0249: the link is STRICT — the creator + the list only, never a visitor's own access. A new
   *  link always is; an older link editing here passes its own flag. */
  strictAudience?: boolean
  setRecipientGrants: (v: RecipientGrants | ((prev: RecipientGrants) => RecipientGrants)) => void
  /** When true, every per-row permission picker is hidden — App-kind
   *  shares are view-only by design (Q5/Q8). */
  isAppShare?: boolean
  isFolder?: boolean
  /** When false, the per-row dropdown's Edit option is replaced with a
   *  red hint explaining the Manage-permission requirement. */
  canGrantEdit?: boolean
  /** This is an external reshare of the caller's own content — the caller is the
   *  Add-to-Drive approver (owner/admin) even though `canGrantEdit` is false
   *  (reshares can't grant edit). Surfaces the approval section (both toggles). */
  isReshare?: boolean
  expiresLocal: string
  setExpiresLocal: (v: string) => void
  /** (0108) Per-permission Add-to-Drive approval bypass. Provided in edit mode;
   *  omitted (create) hides the toggles and the share defaults to approval-
   *  required for both permissions. */
  autoApproveView?: boolean
  setAutoApproveView?: (v: boolean) => void
  autoApproveEdit?: boolean
  setAutoApproveEdit?: (v: boolean) => void
  /** (0119) Hard off-switch for recipient-initiated Add-to-Drive. The
   *  "Allow audience to Add-To-Drive" checkbox is its inverse. */
  forbidAddToDrive?: boolean
  setForbidAddToDrive?: (v: boolean) => void
  appRootFileId?: string | null
  setAppRootFileId?: (v: string | null) => void
  /** Edit mode passes whether `appRootFileId` differs from the saved
   *  value — used to gate the "viewers fetch everything" warning so it
   *  only appears once the user actually changes the folder. Create
   *  mode leaves this undefined, which keeps the warning always-visible
   *  (the user is picking the initial value, the risk is upfront). */
  appRootFileIdDirty?: boolean
  fileId?: string
  /** Edit mode passes the existing link.id so the People list can render
   *  per-row Send Email buttons. Create mode is null — Send is hidden
   *  until after mint (the post-create view re-renders with linkId set). */
  linkId?: string | null
  /** Optional hook fired before any per-row Send Email runs. Edit mode
   *  uses this to auto-save a pending expiry change so the recipient
   *  gets the link with the up-to-date expiry. Returns true when it's
   *  safe to send (no save needed, or save succeeded). Undefined in
   *  Create / PostCreate where there's nothing to auto-save. */
  ensureSaved?: () => Promise<boolean>
  /** Async hook fired when the user submits the Add-recipient modal.
   *  - Create mode: caller stages the grant locally and (if sendQuickLink)
   *    queues the email for delivery after the link mints.
   *  - Edit mode: caller persists the upsert AND sends the quick-link
   *    email immediately. Throwing surfaces the error inside the modal.
   *  When undefined, the default writes the grant into recipientGrants and
   *  ignores sendQuickLink (used only by callers that don't need either). */
  onAddRecipient?: (
    email: string,
    grant: RecipientGrant,
    opts: { sendQuickLink: boolean },
  ) => Promise<void>
  /** Create-mode notify tracking — passed through to RecipientListBlock
   *  so each row's popover renders a checkbox-style toggle for the
   *  staged "email after mint" intent. */
  notifyEmails?: Set<string>
  setNotifyEmail?: (email: string, send: boolean) => void
}) {
  // When the caller picks Edit without canGrantEdit, the row surfaces a
  // red hint and the picker snaps back to view. The flag is row-keyed
  // so multiple rows can show the hint independently.
  const [editBlockedRows, setEditBlockedRows] = useState<Set<string>>(new Set())
  // "Set an expiry date" checkbox — reveals the datetime input. Starts
  // checked only when the link already has an expiry (edit mode); create
  // mode starts unchecked (no expiry). Unchecking clears the value, which
  // is "no expiry" at mint and an expiry-clear on save in edit mode.
  const [expiryEnabled, setExpiryEnabled] = useState<boolean>(() => expiresLocal !== '')
  // "Advanced options" disclosure — folds the Add-to-Drive controls and
  // the expiry date away. Starts open when any of them is already
  // non-default (edit mode), so an active setting is never hidden.
  const [advancedOpen, setAdvancedOpen] = useState<boolean>(() =>
    expiresLocal !== '' || (forbidAddToDrive ?? false) || (autoApproveView ?? false) || (autoApproveEdit ?? false),
  )
  const markBlocked = useCallback((key: string) => {
    setEditBlockedRows((prev) => new Set(prev).add(key))
  }, [])
  const clearBlocked = useCallback((key: string) => {
    setEditBlockedRows((prev) => {
      if (!prev.has(key)) return prev
      const next = new Set(prev)
      next.delete(key)
      return next
    })
  }, [])

  // Decision 10 (+0083): each row owns a {permission, liveMode} grant.
  // Bumping any value writes back to recipient_grants[key]. In Create
  // mode the parent's local state is mutated; in Edit mode the parent's
  // setRecipientGrants implementation fires the PATCH immediately.
  const setRecipientGrant = useCallback((key: string, grant: RecipientGrant) => {
    setRecipientGrants((prev) => ({ ...prev, [key]: grant }))
  }, [setRecipientGrants])
  const removeRecipientKey = useCallback((key: string) => {
    setRecipientGrants((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }, [setRecipientGrants])

  const hasWildcard = '*' in recipientGrants
  const wildcardGrant: RecipientGrant = recipientGrants['*'] ?? { permission: 'view', liveMode: false }
  /** Does the link name anyone? Blocks are denies, so they don't count — same test as
   *  `linkAudience`. Drives the closed button's Private↔Restricted label and
   *  the hint under it. */
  const hasListedRecipient = useMemo(
    () => Object.entries(recipientGrants).some(
      ([k, g]) => k !== '*' && g.permission !== 'block',
    ),
    [recipientGrants],
  )
  const emailKeys = useMemo(
    () => Object.keys(recipientGrants).filter((k) => k !== '*').sort(),
    [recipientGrants],
  )

  return (
    <>
      {/* App root folder picker — appears in both create and edit modes
          for App-kind + non-folder shares. (0084) Edit-mode changes are
          mutable; the server bumps config_version to invalidate active
          sessions so the new root takes effect immediately.
          Outer CollapsibleEdit animates the whole section in/out when
          the user flips App ↔ Source code in CreatePanel; the inner
          one animates the "viewers fetch everything" warning, hidden by
          default in Edit mode until the picker value goes dirty. */}
      {!isFolder && fileId && setAppRootFileId && (
        <CollapsibleEdit isOpen={isAppShare} allowOverflowWhenOpen>
          <Row>
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium themed-text shrink-0">App folder</span>
              <AppRootPicker
                fileId={fileId}
                value={appRootFileId ?? null}
                onChange={setAppRootFileId}
              />
            </div>
            <CollapsibleEdit isOpen={mode === 'create' || appRootFileIdDirty === true}>
              {/* Re-mint note only here: an edit-mode ROOT CHANGE invalidates sessions. */}
              <AppExposureWarning remintNote={mode === 'edit'} />
            </CollapsibleEdit>
          </Row>
        </CollapsibleEdit>
      )}

      {/* Folder (.miniapp) app share, create mode: no App-folder picker (the
          folder IS the root), but the exposure is identical — every file inside
          is fetchable. Same warning, animated with the App/Source pill flip. */}
      {mode === 'create' && isFolder && (
        <CollapsibleEdit isOpen={isAppShare}>
          <Row>
            <AppExposureWarning />
          </Row>
        </CollapsibleEdit>
      )}

      {/* Edit-mode App-kind: static "Run as App" notice (only when there's
          no App folder picker because root is a folder share). */}
      {mode === 'edit' && isAppShare && (isFolder || !fileId || !setAppRootFileId) && (
        <Row>
          <div className="mb-1 text-sm font-medium themed-text">Run as App</div>
          <p className="text-xs pl-1 themed-text-muted leading-relaxed">
            Viewer opens this URL as a PDApp. App shares are view-only.
          </p>
          <AppExposureWarning />
        </Row>
      )}

      {/* Decision 10: live / saved is folded into the per-row picker —
          picking "View (Live Edit)" or "View (Last Saved)" writes both
          recipient_grants[key] AND the link-level liveMode. No standalone
          toggle Row. */}

      {/* Decision 10: General access section — the "*" wildcard slot.
          Public adds the wildcard entry (anyone with the link gets access at
          the picker's permission); Restricted removes it. When Public, the
          permission selector lives beside the choice. App mode hides that
          selector since everyone is view-only. */}
      <Row label={
        <span className="inline-flex items-center gap-2 themed-text">
          <Globe size={18} />
          General access
          {/* Resolved audience — the same derivation the row chip uses, so the heading states
              the CURRENT consequence. A STRICT link (every link minted since 0249, and every
              new one here) reads Restricted even with nobody listed yet; only an older
              non-strict link with nobody listed reads Private. */}
          {(() => {
            const audience = linkAudience(recipientGrants, strictAudience)
            const Icon = audience === 'public' ? Globe : audience === 'restricted' ? Lock : Home
            return (
              <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${AUDIENCE_CHIP_CLS[audience]} text-[10px] font-semibold uppercase tracking-wide`}>
                <Icon size={10} /> {AUDIENCE_LABEL[audience]}
              </span>
            )
          })()}
        </span>
      }>
        {/* Indented under the icon-led label, GDrive-style. */}
        <div className="pl-4">
        {/* min-h reserves the PermissionPicker's height so switching to Public
            (picker appears/disappears) doesn't shift the layout. */}
        <div className="flex items-center justify-between gap-3 flex-wrap min-h-7">
          {/* Two mutually exclusive choices, closed → open so the safe one is under the cursor
              first: Restricted (an email allow-list) and Public (anyone with the link). No Private
              choice — every target already has its Private link (owner, 2026-10-03). */}
          <div
            role="radiogroup"
            aria-label="General access"
            // Sunken track + a solid, GLOWING selected pill. Two earlier
            // attempts under-read on the dark themes: a /30 tint looked like
            // two disabled buttons, and a solid pill on a themed-bg-tertiary
            // track still washed out on dim, whose tertiary (#32324d) is itself
            // blue-violet — the same hue family as both accents, so the fill
            // had almost nothing to contrast against. Fixed on three axes:
            // the track drops to themed-bg-primary (the darkest surface), gets
            // a hairline so its outline is findable at all, and the selected
            // pill carries a tinted drop shadow so it reads as lit rather than
            // merely coloured. Public is the state that must be unmistakable —
            // it is the difference between "my team" and "the internet".
            className="inline-flex items-stretch gap-0.5 rounded-lg p-1 themed-bg-primary themed-border border text-xs"
          >
            {([
              // Restricted (an email allow-list) or Public (the wildcard) — no Private choice:
              // every target already has its Private link (owner, 2026-10-03).
              { key: 'restricted' as const, icon: Lock, on: !hasWildcard, sel: 'bg-purple-500 ring-purple-300/50 shadow-[0_2px_12px_-2px_rgba(168,85,247,0.85)]' },
              { key: 'public' as const, icon: Globe, on: hasWildcard, sel: 'bg-blue-500 ring-blue-300/50 shadow-[0_2px_12px_-2px_rgba(59,130,246,0.85)]' },
            ]).map(({ key, icon: Icon, on, sel }) => (
              <button
                key={key}
                type="button"
                role="radio"
                aria-checked={on}
                title={AUDIENCE_HINT[key]}
                onClick={() => {
                  if (key === 'public') {
                    setRecipientGrant('*', { permission: 'view', liveMode: false })
                  } else {
                    removeRecipientKey('*')
                    clearBlocked('*')
                  }
                }}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md font-semibold transition-all ${on
                  ? `text-white ring-1 ${sel}`
                  : 'themed-text-secondary hover:themed-text hover:themed-bg-hover'}`}
              >
                <Icon size={12} />
                {AUDIENCE_LABEL[key]}
              </button>
            ))}
          </div>
          {hasWildcard && !isAppShare && (
            <PermissionPicker
              permission={wildcardGrant.permission === 'block' ? 'view' : wildcardGrant.permission}
              liveMode={wildcardGrant.liveMode}
              canGrantEdit={canGrantEdit}
              onChange={(sel) => {
                if (sel.permission === 'edit' && !canGrantEdit) {
                  markBlocked('*')
                  return
                }
                clearBlocked('*')
                if (sel.permission === 'edit') {
                  // Edit shares are always live by definition (no merge
                  // path from a snapshot back to the owner's document).
                  setRecipientGrant('*', { permission: 'edit', liveMode: true })
                } else if (sel.permission === 'view') {
                  setRecipientGrant('*', { permission: 'view', liveMode: sel.liveMode })
                }
                // 'block' is suppressed for the wildcard (allowBlock=false).
              }}
              isFolder={isFolder}
              idPrefix={`${idPrefix}-wildcard`}
              // The wildcard never exposes 'block' (blocking anyone-with-
              // link is just unchecking the box). Per-row pickers do.
              allowBlock={false}
              size="sm"
            />
          )}
        </div>
        {editBlockedRows.has('*') ? (
          <p className="text-xs pl-1 mt-2 text-red-400 leading-relaxed">
            You need 'Manage' permission to grant Edit access.
          </p>
        ) : (
          <p className="text-xs pl-1 mt-2 themed-text-muted leading-relaxed">
            {(() => {
              // What the visitors get (owner, 2026-10-03) — the SAME note for Restricted and Public:
              // a PDApp (Mode B) runs as the app, live — the same for everyone; a loose-HTML app
              // (Mode A) and a source link carry YOUR view as of creation (a snapshot), so later
              // changes need the link extended.
              const snapshot = 'They see what you see — and nothing you can’t — as of when the link is created; a new shortcut (.chain) or a restriction lifted after that needs the link extended for them.'
              // #1080: an app link always shows the SAVED files — say so, since a live Source choice
              // isn't carried into it.
              const savedNote = 'Visitors see your last saved version, never unsaved edits.'
              const typeNote = isAppShare && isFolder
                ? `Everyone gets the same experience — the app runs as the author intended. ${savedNote}`
                : isAppShare
                  // Mode A (loose HTML): edits happen through the page's code (the SDK).
                  ? `${savedNote} ${snapshot} Visitors who can edit through their own access can edit via the SDK if the page provides scripts or SQL.`
                  : `${snapshot} Visitors with edit permission, by the link or by their own access, can edit.`
              if (!hasWildcard) {
                // An older (pre-0249) link naming nobody still opens for whoever already has
                // access (the native floor, linkCarriesNativeFloor).
                if (!hasListedRecipient && !strictAudience) {
                  return 'Opens only for people who already have permission to this folder. Add people below to limit it to just them.'
                }
                const first = hasListedRecipient ? 'Only the people you list below can open it.' : 'Add the people who can open it below.'
                return `${first} ${typeNote}`
              }
              const first = isAppShare
                ? 'Anyone with the URL can open the app.'
                : wildcardGrant.permission === 'edit'
                  ? (isFolder ? 'Anyone with the URL can edit any file in this folder.' : 'Anyone with the URL can edit the file.')
                  : wildcardGrant.liveMode
                    ? 'Anyone with the URL sees your edits in real time.'
                    : 'Anyone with the URL sees your most recent save.'
              return `${first} ${typeNote}`
            })()}
          </p>
        )}
        </div>
      </Row>

      {/* Per-recipient cards. The label tracks the wildcard state: with
          "Anyone with the link" OFF the emails ARE the gate (an
          allow-list); with it ON everyone gets in anyway and the emails
          only carry per-person overrides. */}
      <Row label={
        <span className="inline-flex items-center gap-2 themed-text">
          <Mail size={18} />
          {hasWildcard ? 'Email-specific access' : 'Email allow-list'}
        </span>
      }>
        <RecipientListBlock
          linkId={linkId}
          emails={emailKeys}
          grants={recipientGrants}
          isFolder={isFolder}
          isAppShare={isAppShare}
          canGrantEdit={canGrantEdit}
          editBlockedRows={editBlockedRows}
          markBlocked={markBlocked}
          clearBlocked={clearBlocked}
          // Modal always shows the "Email quick link" checkbox (default on).
          // - Create mode: checkbox staged; emails fire after mint.
          // - Edit mode: checkbox staged; if on, save-then-send fires
          //   immediately. Unchecked = quietly add the recipient, no email.
          showAddEmailCheckbox={true}
          notifyEmails={notifyEmails}
          setNotifyEmail={setNotifyEmail}
          onAdd={onAddRecipient ?? (async (email, grant) => {
            // Default fallback: pure local stage (used in tests / future
            // callers that don't need send-on-add semantics).
            setRecipientGrant(email.toLowerCase(), grant)
          })}
          onRemove={(email) => {
            removeRecipientKey(email)
          }}
          onPermissionChange={(email, sel) => {
            // Per-row picker writes the recipient's grant directly:
            // permission + per-recipient liveMode.
            if (sel.permission === 'edit') {
              setRecipientGrant(email, { permission: 'edit', liveMode: true })
            } else if (sel.permission === 'block') {
              setRecipientGrant(email, { permission: 'block', liveMode: false })
            } else {
              setRecipientGrant(email, { permission: 'view', liveMode: sel.liveMode })
            }
          }}
          hasWildcard={hasWildcard}
          strictAudience={strictAudience}
          idPrefix={idPrefix}
          ensureSaved={ensureSaved}
        />
      </Row>

      {/* Advanced options disclosure — the Add-to-Drive controls and the
          expiry date live behind it (see advancedOpen). */}
      <Row>
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          aria-expanded={advancedOpen}
          className="flex items-center gap-1.5 text-sm themed-text-muted cursor-pointer hover:themed-text"
        >
          <ChevronDown size={14} className={`transition-transform ${advancedOpen ? '' : '-rotate-90'}`} />
          Advanced options
        </button>
      </Row>

      {/* The 0fr→1fr grid-rows transition animates expand/collapse while
          the content stays mounted (an unmount would snap shut with no
          exit animation); `invisible` when closed keeps the collapsed
          controls out of focus order and hit-testing. */}
      <div
        className={`grid transition-[grid-template-rows,visibility] duration-200 ease-out ${
          advancedOpen ? 'grid-rows-[1fr] visible' : 'grid-rows-[0fr] invisible'
        }`}
        aria-hidden={!advancedOpen}
      >
        {/* pl-4 indents the folded rows under the disclosure toggle. */}
        <div className="overflow-hidden min-h-0 pl-4">
      {/* (0108/0119) Add-to-Drive controls (create + edit). Hidden for app
          shares — they're deployed runtimes, not mountable files. Shown to the
          Add-to-Drive APPROVER: owner/admin of the content (canGrantEdit), OR
          the content owner managing an external reshare of their content
          (isReshare — the link was created by an outsider, but the OWNER still
          approves its mounts and can grant edit on approval). A plain member /
          outsider who only holds the folder via a mount or a non-admin grant
          sees neither — they can only mint read-access links. The checkbox is
          the inverse of the persisted forbid_add_to_drive hard off-switch;
          unchecking it also withdraws any auto-approve bypass. */}
      {setAutoApproveView && setAutoApproveEdit && setForbidAddToDrive && !isAppShare && (canGrantEdit || isReshare) && (
        <Row>
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input
              type="checkbox"
              checked={!(forbidAddToDrive ?? false)}
              onChange={(e) => {
                setForbidAddToDrive(!e.target.checked)
                if (!e.target.checked) {
                  // Forbidding withdraws any bypass too, so re-allowing
                  // later lands back on the confirm-every-request default.
                  setAutoApproveView(false)
                  setAutoApproveEdit(false)
                }
              }}
              className="accent-blue-500 w-3.5 h-3.5"
            />
            <span className="themed-text text-sm">Allow audience to Add-To-Drive</span>
          </label>
          {!(forbidAddToDrive ?? false) && (
            <div className="pl-5 mt-2">
              <p className="text-xs themed-text-muted mb-2">
                Choose who can add this to their Drive without asking you first.
              </p>
              <button
                type="button"
                role="switch"
                aria-checked={autoApproveView ?? false}
                aria-label="Auto-approve viewers"
                onClick={() => setAutoApproveView(!(autoApproveView ?? false))}
                className="w-full flex items-start gap-3 text-left cursor-pointer"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm themed-text">Auto-approve viewers</span>
                  <span className="block text-xs themed-text-muted">People with view access can add it right away.</span>
                </span>
                <Switch checked={autoApproveView ?? false} className="mt-0.5" />
              </button>
              <button
                type="button"
                role="switch"
                aria-checked={autoApproveEdit ?? false}
                aria-label="Auto-approve editors"
                onClick={() => setAutoApproveEdit(!(autoApproveEdit ?? false))}
                className="w-full flex items-start gap-3 text-left cursor-pointer mt-2.5"
              >
                <span className="min-w-0 flex-1">
                  <span className="block text-sm themed-text">Auto-approve editors</span>
                  <span className="block text-xs themed-text-muted">People with edit access can add it right away, and can make changes.</span>
                </span>
                <Switch checked={autoApproveEdit ?? false} className="mt-0.5" />
              </button>
            </div>
          )}
        </Row>
      )}

      <Row>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={expiryEnabled}
            onChange={(e) => {
              setExpiryEnabled(e.target.checked)
              if (!e.target.checked) setExpiresLocal('')
            }}
            className="accent-blue-500 w-3.5 h-3.5"
          />
          <span className="themed-text text-sm">Set an expiry date</span>
        </label>
        {expiryEnabled && (
          <div className="pl-5 mt-2">
            <input
              id={`${idPrefix}-expires`}
              type="datetime-local"
              aria-label="Expires at"
              value={expiresLocal}
              onChange={(e) => setExpiresLocal(e.target.value)}
              style={{ colorScheme: 'dark' }}
              className="themed-input px-2.5 py-2 text-xs rounded border themed-border themed-bg-primary"
            />
          </div>
        )}
      </Row>
        </div>
      </div>
    </>
  )
}

// ─── Per-row permission picker ──────────────────────────────────────
//
// Decision 10 / Q3: every recipient row (including the "*" wildcard)
// has its own dropdown. GDrive-style: two-line items (label + muted
// description) revealed on click. Verb form per user lock.
//
// Live mode is link-level but folded into the picker — picking
// "View (Live Edit)" or "View (Last Saved)" writes to share_links.liveMode
// in addition to recipient_grants[key]. The Edit option doesn't touch
// liveMode (the server treats edit as always live regardless of flag).
//
// Picker selections (0083: live/saved variants are always available;
// no isLiveEligible gating — future file types can opt in without
// re-shipping the picker):
//   - View (Last Saved) — permission='view',  liveMode=false
//   - View (Live Edit)  — permission='view',  liveMode=true
//   - Edit              — permission='edit'                     (only when canGrantEdit)
//   - Block             — permission='block'                    (per-row picker only)

type PickerSelection =
  | { permission: 'view'; liveMode: false }
  | { permission: 'view'; liveMode: true }
  | { permission: 'edit' }
  | { permission: 'block' }

type PickerOptionKind = 'view-saved' | 'view-live' | 'edit' | 'block'

/**
 * Per-row action surfaced inside PermissionPicker / AppRowActionsMenu.
 * Two shapes:
 *  - 'button' (default): full-width menu item with icon, label, optional
 *    description below — used for one-shot actions (Send Email, Remove).
 *  - 'checkbox': compact label + native <input type="checkbox"> with no
 *    item padding or desc. Used for staged toggles (e.g. create-mode
 *    "email quick link after mint") so the affordance reads as a real
 *    switch instead of a clickable row.
 */
type RowActionItem =
  | {
      kind?: 'button'
      key: string
      label: string
      icon: ReactNode
      onClick: () => void
      danger?: boolean
      disabled?: boolean
      desc?: string
    }
  | {
      kind: 'checkbox'
      key: string
      label: string
      checked: boolean
      onChange: (next: boolean) => void
      disabled?: boolean
    }

/** Trigger glyph for the chosen permission. Eye = view (last saved —
 *  the "scan frame" reads as a point-in-time snapshot), Eye = view (live
 *  edits, with a green pulse dot), SquarePen = edit, Ban = block. */
function PermissionGlyph({ kind, size = 12 }: { kind: PickerOptionKind; size?: number }) {
  if (kind === 'edit') {
    return <SquarePen size={size} className="themed-text" />
  }
  if (kind === 'block') {
    return <Ban size={size} className="themed-text" />
  }
  if (kind === 'view-live') {
    return (
      <span className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
        <Eye size={size} className="themed-text" />
        <span
          className="absolute -top-0.5 -right-0.5 rounded-full bg-emerald-400"
          style={{ width: 5, height: 5 }}
          aria-hidden="true"
        />
      </span>
    )
  }
  return <Eye size={size} className="themed-text" />
}

/**
 * Position a portal'd popover against its trigger, kept inside the viewport.
 *
 * Both menus in this file (the recipient PermissionPicker and the App row's
 * kebab) portal to `document.body` and position from the trigger's rect,
 * because the dialog nests several `overflow-hidden` containers that would
 * otherwise clip an absolutely-positioned dropdown. They carried a copy each
 * of the placement maths — and both copies clamped only HORIZONTALLY, pinning
 * `top` to `rect.bottom + GAP` unconditionally. A row near the bottom of the
 * screen therefore opened a menu whose lower half was off-screen and
 * unreachable: the popover is `fixed`, so no amount of scrolling reveals it.
 *
 * Placement: below the trigger by default, flipped above when it does not fit,
 * and — when it fits neither side — pinned to the roomier one with a maxHeight
 * so it scrolls internally instead of overflowing.
 *
 * Returns the refs to attach: `triggerRef` on the button, `menuRef` on the
 * popover itself (NOT a wrapper — a wrapper around a `fixed` child measures as
 * zero-height, which would silently disable the flip).
 */
function usePortalMenuPosition(open: boolean, width = 230) {
  const [menuStyle, setMenuStyle] = useState<{ top: number; left: number; width: number; maxHeight?: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const placeMenu = useCallback(() => {
    if (!triggerRef.current) return
    const rect = triggerRef.current.getBoundingClientRect()
    const MARGIN = 8  // keep this much clear of the viewport edges
    const GAP = 4     // breathing room between trigger and menu

    // Anchor to the trigger's right edge, clamped inside the viewport.
    let left = rect.right - width
    if (left + width > window.innerWidth - MARGIN) left = window.innerWidth - width - MARGIN
    if (left < MARGIN) left = MARGIN

    // scrollHeight, not offsetHeight: once a maxHeight is applied below, the
    // offset height IS that cap, so re-measuring through it on the next
    // scroll/resize pass would ratchet the menu smaller every time.
    const height = menuRef.current?.scrollHeight ?? 0
    const below = window.innerHeight - rect.bottom - GAP - MARGIN
    const above = rect.top - GAP - MARGIN
    let top = rect.bottom + GAP
    let maxHeight: number | undefined
    if (height > below) {
      if (height <= above) {
        top = rect.top - GAP - height   // flip above the trigger
      } else if (above > below) {
        top = MARGIN                    // neither side fits: take the roomier
        maxHeight = above               // one and scroll inside
      } else {
        maxHeight = below
      }
    }
    setMenuStyle({ top, left, width, maxHeight })
  }, [width])

  useLayoutEffect(() => {
    if (!open) {
      // Drop the position so the next open re-runs the two-pass measure from
      // scratch rather than reusing the previous placement.
      setMenuStyle(null)
      return
    }
    placeMenu()
  }, [open, placeMenu])

  // Second pass. The first runs before the portal exists, so `menuRef` is null
  // and the menu is placed unmeasured (i.e. always below). Once `menuStyle` is
  // set the popover is mounted and measurable, so re-place with the real
  // height. Keyed on the null→non-null transition, which fires exactly once per
  // open; both passes are layout effects, so only the corrected position paints.
  const placed = menuStyle !== null
  useLayoutEffect(() => {
    if (!open || !placed) return
    placeMenu()
  }, [open, placed, placeMenu])

  // Reposition on scroll / resize while open so the menu stays glued to the
  // trigger if the user scrolls the dialog body.
  useEffect(() => {
    if (!open) return
    const onScroll = () => placeMenu()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [open, placeMenu])

  return { menuStyle, triggerRef, menuRef }
}

/** Inline style for a popover positioned by `usePortalMenuPosition`. The
 *  internal scroll only engages when a maxHeight was actually needed. */
function portalMenuStyle(s: { top: number; left: number; width: number; maxHeight?: number }) {
  return s.maxHeight ? { ...s, overflowY: 'auto' as const } : s
}

function PermissionPicker({
  permission,
  liveMode,
  onChange,
  isFolder,
  idPrefix,
  size = 'sm',
  compact = false,
  actions,
  allowBlock = true,
  canGrantEdit = true,
}: {
  permission: 'view' | 'edit' | 'block'
  liveMode: boolean
  onChange: (sel: PickerSelection) => void
  isFolder: boolean
  idPrefix: string
  size?: 'sm' | 'md'
  /** Owner/admin-only: when false the Edit option is OMITTED from the dropdown
   *  entirely (not just disabled). A resharer / non-admin member can only pick
   *  View/Block — they can't delegate write access via a link. */
  canGrantEdit?: boolean
  /** Narrow container — collapse the trigger to icon-only. The dropdown
   *  menu still shows full two-line items. */
  compact?: boolean
  /** Whether to surface the 'Blocked' option. Per-row pickers set
   *  true; the wildcard "Anyone with the link" picker sets false
   *  (blocking everyone is just unchecking the box). */
  allowBlock?: boolean
  /** Optional row actions rendered as menu items below the permission
   *  options (divider above). GDrive parity: Remove access, Email Quick
   *  Link, etc. live in the same popover as the permission picker.
   *  Items with `kind:'checkbox'` render as compact <input type="checkbox">
   *  switches instead of full menu rows — see RowActionItem. */
  actions?: RowActionItem[]
}) {
  // The dialog has nested overflow-hidden containers (RecipientListBlock
  // for rounded corners, the scroll body for the panel). An absolutely-
  // positioned dropdown gets clipped by any of those. Portal-mount the
  // menu under document.body and position it from the trigger's rect.
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [open, setOpen] = useState(false)
  const { menuStyle, triggerRef, menuRef } = usePortalMenuPosition(open)

  // Close on outside click — outside means neither the trigger nor the
  // portal'd menu contains the click target.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    const id = requestAnimationFrame(() => window.addEventListener('click', handler))
    return () => {
      cancelAnimationFrame(id)
      window.removeEventListener('click', handler)
    }
  }, [open])

  const currentKind: PickerOptionKind = permission === 'edit'
    ? 'edit'
    : permission === 'block'
      ? 'block'
      : liveMode ? 'view-live' : 'view-saved'

  const options: Array<{ kind: PickerOptionKind; label: string; desc: string }> = []
  options.push({
    kind: 'view-saved',
    label: 'View (Last Saved)',
    desc: 'See the latest saved version.',
  })
  options.push({
    kind: 'view-live',
    label: 'View (Live Edit)',
    desc: 'See edits in real time.',
  })
  // Edit is owner/admin-only — omit it entirely for callers who can't grant it
  // (resharer / non-admin member), so the dropdown offers View/Block only.
  if (canGrantEdit) {
    options.push({
      kind: 'edit',
      label: 'Edit',
      desc: isFolder ? 'Edit files in this folder.' : 'Edit the file.',
    })
  }
  if (allowBlock) {
    options.push({
      kind: 'block',
      label: 'Blocked',
      desc: 'Deny access to this person.',
    })
  }

  const triggerSizeCls = compact
    ? (size === 'sm' ? 'pl-1 pr-4 py-0.5' : 'pl-1.5 pr-5 py-1')
    : (size === 'sm' ? 'pl-1.5 pr-5 py-0.5 text-sm' : 'pl-2 pr-7 py-1 text-base')
  const chevPos = compact
    ? (size === 'sm' ? 'right-0.5' : 'right-1')
    : (size === 'sm' ? 'right-1' : 'right-2')
  const chevSize = size === 'sm' ? 14 : 16
  const glyphSize = size === 'sm' ? 14 : 16

  const triggerLabel = options.find(o => o.kind === currentKind)?.label ?? 'View'

  const handlePick = (kind: PickerOptionKind) => {
    setOpen(false)
    if (kind === 'edit') {
      onChange({ permission: 'edit' })
    } else if (kind === 'block') {
      onChange({ permission: 'block' })
    } else if (kind === 'view-live') {
      onChange({ permission: 'view', liveMode: true })
    } else {
      onChange({ permission: 'view', liveMode: false })
    }
  }

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        id={`${idPrefix}-perm`}
        type="button"
        onClick={() => setOpen(v => !v)}
        className={`themed-text ${triggerSizeCls} font-medium rounded-md cursor-pointer bg-transparent border-0 themed-bg-hover transition-colors text-left relative whitespace-nowrap inline-flex items-center gap-1.5`}
        title={compact ? triggerLabel : undefined}
        aria-label={compact ? `Permission: ${triggerLabel}` : undefined}
      >
        <PermissionGlyph kind={currentKind} size={glyphSize} />
        {!compact && <span>{triggerLabel}</span>}
        <ChevronDown size={chevSize} className={`absolute ${chevPos} top-1/2 -translate-y-1/2 pointer-events-none themed-text-muted`} />
      </button>
      {open && menuStyle && createPortal(
        <div className={themeClass}>
          <div
            ref={menuRef}
            className={`fixed py-0.5 themed-bg-secondary border themed-border-secondary rounded-md shadow-lg ${SHARE_DIALOG_MENU_Z}`}
            style={portalMenuStyle(menuStyle)}
          >
            {options.map((opt) => {
              const isActive = opt.kind === currentKind
              return (
                <button
                  key={opt.kind}
                  type="button"
                  onClick={() => handlePick(opt.kind)}
                  className={`w-full text-left px-2 py-1.5 text-[11px] cursor-pointer themed-bg-hover ${isActive ? 'themed-text' : 'themed-text-secondary'}`}
                  title={opt.desc}
                >
                  <div className="flex items-center gap-1.5">
                    {isActive
                      ? <Check size={11} className="themed-text-muted shrink-0" />
                      : <span className="inline-block w-[11px] shrink-0" />}
                    <PermissionGlyph kind={opt.kind} size={11} />
                    <span className="font-medium">{opt.label}</span>
                  </div>
                  <div className="pl-[30px] themed-text-muted leading-tight mt-0.5 text-[10px]">
                    {opt.desc}
                  </div>
                </button>
              )
            })}
            {actions && actions.length > 0 && (
              <>
                <div className="my-0.5 border-t themed-border" />
                {actions.map((act) => (
                  act.kind === 'checkbox' ? (
                    <label
                      key={act.key}
                      className={`flex items-center gap-2 pl-3.5 pr-2 py-1.5 text-[11px] cursor-pointer themed-bg-hover ${act.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={act.checked}
                        disabled={act.disabled}
                        onChange={(e) => act.onChange(e.target.checked)}
                        className="accent-blue-500 w-3.5 h-3.5 shrink-0"
                      />
                      <span className="themed-text-secondary font-medium">{act.label}</span>
                    </label>
                  ) : (
                    <button
                      key={act.key}
                      type="button"
                      disabled={act.disabled}
                      onClick={() => { setOpen(false); act.onClick() }}
                      className={`w-full text-left px-2 py-1.5 text-[11px] cursor-pointer themed-bg-hover disabled:opacity-50 disabled:cursor-not-allowed ${act.danger ? 'text-red-400 hover:text-red-300' : 'themed-text-secondary'}`}
                      title={act.desc}
                    >
                      <div className="flex items-center gap-1.5">
                        <span className="inline-block w-[11px] shrink-0" />
                        <span className="shrink-0 inline-flex items-center justify-center" style={{ width: 11, height: 11 }}>
                          {act.icon}
                        </span>
                        <span className="font-medium">{act.label}</span>
                      </div>
                      {act.desc && (
                        <div className="pl-[30px] themed-text-muted leading-tight mt-0.5 text-[10px]">
                          {act.desc}
                        </div>
                      )}
                    </button>
                  )
                ))}
              </>
            )}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

// ─── helpers ─────────────────────────────────────────────────────────

interface StatusBadge { label: string; cls: string }

function describeStatus(link: ShareLinkWithUrl): StatusBadge {
  // A plain helper, not a component, so it reads the brand STORE rather than
  // the hook. The store always holds a valid snapshot (see `useBrand`'s own
  // fallback), and a status label does not need to re-render on a brand change.
  const { productName } = getBrandSnapshot().identity
  if (link.isRevoked) {
    // Decision 9 / Q6: revoke_reason distinguishes manual revoke from
    // auto-revoke on source-file hard delete. Surfaces in the badge so
    // the owner knows why their link stopped working.
    if (link.revokeReason === 'file_deleted') {
      return { label: 'File deleted', cls: 'bg-red-500/20 text-red-200' }
    }
    // 'abuse' is set by scripts/abuse/disable-share.mjs (ops takedown
    // path). Show a distinct label so the owner doesn't mistake this
    // for their own action. Details live in share_link_abuse_actions
    // (audit log); the owner contacts support if they want to dispute.
    if (link.revokeReason === 'abuse') {
      return { label: `Disabled by ${productName}`, cls: 'bg-red-500/20 text-red-200' }
    }
    return { label: 'Revoked', cls: 'bg-red-500/20 text-red-200' }
  }
  if (link.expiresAt && new Date(link.expiresAt).getTime() < Date.now()) {
    return { label: 'Expired', cls: 'bg-yellow-500/20 text-yellow-200' }
  }
  return { label: 'Active', cls: 'bg-green-500/20 text-green-200' }
}

const RELATIVE_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['second', 1],
  ['minute', 60],
  ['hour', 3600],
  ['day', 86400],
  ['week', 604800],
  ['month', 2629800],
  ['year', 31557600],
]

function formatRelative(iso: string): string {
  const target = new Date(iso).getTime()
  if (Number.isNaN(target)) return iso
  const diffSec = Math.round((target - Date.now()) / 1000)
  const absSec = Math.abs(diffSec)
  let unit: Intl.RelativeTimeFormatUnit = 'second'
  let divisor = 1
  for (const [u, d] of RELATIVE_UNITS) {
    if (absSec >= d) { unit = u; divisor = d }
    else break
  }
  const value = Math.round(diffSec / divisor)
  return new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' }).format(value, unit)
}

function isoToLocalInput(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  // datetime-local wants YYYY-MM-DDTHH:mm (no seconds, no TZ)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function localInputToIso(local: string): string | null {
  if (!local) return null
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

// ─── Create panel ────────────────────────────────────────────────────
//
// Collapsed by default — the toolbar button toggles it. The panel shape:
//
//   • "Share PDApp" toggle switch (iff App-eligible — HTML / .miniapp).
//     Explanatory subtext shifts based on the chosen state.
//   • "Updates" two-button picker (iff live-eligible — collab surfaces).
//     Subtext under the pair explains the chosen option.
//   • "Access" two-button picker (always). Same subtext-after-choice
//     pattern.
//   • Expires at input (always).
//
// All settings bake into the new link at mint; kind is immutable
// post-mint. liveMode / accessMode / expiresAt stay editable per row
// via the inline pencil.

function CreatePanel({
  isAppEligible,
  isFolder,
  canGrantEdit,
  runAsApp,
  setRunAsApp,
  createExpiresLocal,
  setCreateExpiresLocal,
  createRecipientGrants,
  setCreateRecipientGrants,
  createAppRootFileId,
  setCreateAppRootFileId,
  createAutoApproveView,
  setCreateAutoApproveView,
  createAutoApproveEdit,
  setCreateAutoApproveEdit,
  createForbidAddToDrive,
  setCreateForbidAddToDrive,
  fileId,
  creating,
  appBlocked = false,
  onCancel,
  onCreate,
  stageNotifyEmail,
  notifyEmails,
}: {
  isAppEligible: boolean
  isFolder: boolean
  /** Server-resolved: caller's permission on the file is >= admin. When
   *  false, the Edit option is hidden from every per-row permission picker. */
  canGrantEdit: boolean
  runAsApp: boolean
  setRunAsApp: (v: boolean) => void
  createExpiresLocal: string
  setCreateExpiresLocal: (v: string) => void
  /** Decision 10: unified recipient_grants map (emails + optional "*").
   *  Per-row permission lives here, not in a top-level Permission picker. */
  createRecipientGrants: RecipientGrants
  setCreateRecipientGrants: (v: RecipientGrants | ((prev: RecipientGrants) => RecipientGrants)) => void
  /** App root selection for kind='app' + loose HTML files. NULL = let
   *  the server default to the file's parent folder. */
  createAppRootFileId: string | null
  setCreateAppRootFileId: (v: string | null) => void
  /** (0108/0119) Add-to-Drive controls staged for the mint. */
  createAutoApproveView: boolean
  setCreateAutoApproveView: (v: boolean) => void
  createAutoApproveEdit: boolean
  setCreateAutoApproveEdit: (v: boolean) => void
  createForbidAddToDrive: boolean
  setCreateForbidAddToDrive: (v: boolean) => void
  /** Used to fetch the ancestor list for the Root folder picker. */
  fileId: string
  creating: boolean
  /** The page's folder is forbidden (#889): an app link can't be made, so Create is
   *  disabled on the PDApp option with the reason. */
  appBlocked?: boolean
  onCancel: () => void
  onCreate: () => void
  /** Stage (or clear) the post-mint email-quick-link intent for one
   *  recipient. The parent dialog fans out the actual sends after the
   *  link mints. */
  stageNotifyEmail: (email: string, send: boolean) => void
  /** Mirror of the parent dialog's `createNotifyEmails` so per-row
   *  popovers can render the toggle in its current state. */
  notifyEmails: Set<string>
}) {
  return (
    // Green border distinguishes the Create panel from the otherwise-
    // identical Edit panel (same form body, same card shape). The
    // accent color signals "you're minting something new" vs "you're
    // patching an existing link".
    <div className="border border-emerald-500/60 rounded-xl themed-bg-secondary overflow-hidden shadow-[0_0_16px_-4px_rgba(16,185,129,0.25)]">
      {isAppEligible && (
        <Row>
          {/* Glass mode-pill (same design as the default-link card's App|Source
              toggle + the editor toolbar): amber chip for App, indigo for source. */}
          <GlassToggle
            value={runAsApp ? 'app' : 'source'}
            onChange={(v) => setRunAsApp(v === 'app')}
            options={[
              { value: 'app', label: 'Share PDApp', accent: '#d38c25' },
              { value: 'source', label: isFolder ? 'Share folder' : 'Share source code' },
            ]}
          />
          <p className="text-xs pl-1 themed-text-muted leading-relaxed mt-2">
            {runAsApp
              ? 'Viewer opens this URL as a PDApp. User data stays protected.'
              : isFolder
                ? 'Viewer browses the folder contents in the previewer.'
                : 'Viewer sees the source rendered in the previewer.'}
          </p>
        </Row>
      )}

      <ShareLinkOptionsBody
        mode="create"
        idPrefix="create"
        // Every new link is STRICT (0249) — said here, not left to the prop's default, so the
        // chip this body draws and Create's own check (`linkNamesNobody`) read the same link.
        strictAudience
        recipientGrants={createRecipientGrants}
        setRecipientGrants={setCreateRecipientGrants}
        expiresLocal={createExpiresLocal}
        setExpiresLocal={setCreateExpiresLocal}
        isAppShare={runAsApp}
        isFolder={isFolder}
        canGrantEdit={canGrantEdit}
        autoApproveView={createAutoApproveView}
        setAutoApproveView={setCreateAutoApproveView}
        autoApproveEdit={createAutoApproveEdit}
        setAutoApproveEdit={setCreateAutoApproveEdit}
        forbidAddToDrive={createForbidAddToDrive}
        setForbidAddToDrive={setCreateForbidAddToDrive}
        appRootFileId={createAppRootFileId}
        setAppRootFileId={setCreateAppRootFileId}
        fileId={fileId}
        onAddRecipient={async (email, grant, { sendQuickLink }) => {
          // Create mode: stage locally, queue the post-mint email if the
          // modal's checkbox was on. The link doesn't exist yet, so the
          // actual send happens in ShareLinksDialog.handleCreate after
          // mintShareLink returns.
          const key = email.toLowerCase()
          setCreateRecipientGrants((prev) => ({ ...prev, [key]: grant }))
          stageNotifyEmail(key, sendQuickLink)
        }}
        notifyEmails={notifyEmails}
        setNotifyEmail={stageNotifyEmail}
      />

      {runAsApp && appBlocked && (
        <p className="flex items-start gap-1.5 px-3 pt-3 text-xs text-amber-300/90 [.theme-light_&]:text-amber-800 leading-relaxed" data-testid="create-app-forbidden">
          <Lock size={12} className="shrink-0 mt-0.5" />
          <span><span className="font-semibold">Forbidden folder.</span> Move the page into its own folder first, or share the source code instead.</span>
        </p>
      )}
      <div className="flex justify-end gap-2 p-3 ws-drive-pill">
        <button
          type="button"
          onClick={onCancel}
          disabled={creating}
          className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium themed-text rounded-md themed-bg-hover disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onCreate}
          // A new link is STRICT, so with nobody listed its chip reads Restricted (as the
          // options body shows it). A Restricted link must name someone — one naming nobody
          // would only duplicate the target's Private link (owner, 2026-10-03).
          disabled={creating || (runAsApp && appBlocked) || linkNamesNobody(createRecipientGrants)}
          title={linkNamesNobody(createRecipientGrants) ? 'A Restricted link must name someone — add at least one person, or make it Public' : undefined}
          data-testid="create-link-confirm"
          className="themed-btn-primary inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-medium rounded-md disabled:opacity-50"
        >
          {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
          Create link
        </button>
      </div>
    </div>
  )
}

// ─── Form primitives ─────────────────────────────────────────────────
//
// Row groups one control with an optional label + hint, separated by
// dividers from siblings. Keeps the visual rhythm consistent so the eye
// can scan section by section.

function Row({
  label,
  hint,
  children,
}: {
  label?: ReactNode
  hint?: string
  children: ReactNode
}) {
  return (
    <div className="p-4 space-y-2">
      {label && <Label>{label}</Label>}
      {children}
      {hint && <p className="text-xs pl-1 themed-text-muted leading-relaxed">{hint}</p>}
    </div>
  )
}

function Label({ children, htmlFor }: { children: ReactNode; htmlFor?: string }) {
  return (
    <label htmlFor={htmlFor} className="block text-sm font-medium themed-text mb-4">
      {children}
    </label>
  )
}

/** The toggle track + sliding knob shared by every `role="switch"` in this
 *  dialog (the footer "Show inactive" and the Add-to-Drive approval toggles).
 *  The caller owns the `role="switch"` button + label layout; this is just the
 *  pill. `className` lets a caller tweak alignment (e.g. `mt-0.5`). */
function Switch({ checked, className = '' }: { checked: boolean; className?: string }) {
  return (
    <span className={`relative w-9 h-5 rounded-full transition-colors shrink-0 shadow-[inset_0_1px_3px_rgba(0,0,0,0.2)] ${checked ? 'bg-violet-500' : 'themed-bg-primary'} ${className}`}>
      <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.35)] transition-all ${checked ? 'left-[18px]' : 'left-0.5'}`} />
    </span>
  )
}


/**
 * Phase 1 App-root picker (create-mode + HTML file only).
 *
 * Mirrors the HtmlEditor's root dropdown shape: indented hierarchy of
 * accessible ancestors (papan at top → file's parent at bottom), with
 * the active selection check-marked. Selection is purely local — it
 * writes to share_links.app_root_file_id at mint time and does NOT
 * touch preview_html_config (so the editor's reference root stays
 * org-wide while each share carries its own runtime scope).
 *
 * Default visible selection is the file's immediate parent (server-
 * returned `defaultFolderId`), even when `value` is null — that's
 * what the share will mint as if the user doesn't change it.
 */
function AppRootPicker({
  fileId,
  value,
  onChange,
}: {
  fileId: string
  /** NULL = user hasn't picked yet; picker still displays the default. */
  value: string | null
  onChange: (folderId: string | null) => void
}) {
  const [opts, setOpts] = useState<AppRootOption[]>([])
  const [defaultId, setDefaultId] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    fetchAppRootOptions(fileId)
      .then((r) => {
        if (cancelled) return
        setOpts(r.options)
        // The share default skips the drive's own folders (#889): an older server
        // sends no `shareDefaultFolderId` — then the plain default.
        setDefaultId(r.shareDefaultFolderId !== undefined ? r.shareDefaultFolderId : r.defaultFolderId)
      })
      .catch(() => {
        if (!cancelled) setOpts([])
      })
    return () => { cancelled = true }
  }, [fileId])

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    const id = requestAnimationFrame(() => window.addEventListener('click', handler))
    return () => {
      cancelAnimationFrame(id)
      window.removeEventListener('click', handler)
    }
  }, [open])

  // Server returns ancestors immediate-parent first. Reverse so the
  // dropdown reads top→bottom as papan → file's folder, matching the
  // HtmlEditor picker shape.
  const orderedAncestors = useMemo(() => [...opts].reverse(), [opts])
  const activeId = value ?? defaultId
  const activeOpt = opts.find((o) => o.folderId === activeId) ?? null

  return (
    <div ref={menuRef} className="relative">
      {/* Glass chip trigger — same quiet ws-tool-btn as the editor toolbar
          (replaces the old bordered themed-input button), with an amber glow
          tying it to the App accent (#d38c25). */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={opts.length === 0}
        className="ws-tool-btn cursor-pointer disabled:opacity-50 disabled:cursor-default border-amber-400/50 shadow-[0_0_12px_-2px_rgba(211,140,37,0.55)] hover:shadow-[0_0_14px_-1px_rgba(211,140,37,0.7)] transition-shadow"
        aria-expanded={open}
      >
        <FolderTree size={14} className="themed-text-muted shrink-0" />
        <span className="truncate max-w-[200px]">
          {/* The drive's own folders read "Forbidden folder" (owner wording): the page's folder
              when it is one of them, or an old link still sitting on one. */}
          {activeOpt?.shareable === false || (!activeOpt && opts.length > 0 && !opts.some((o) => o.shareable !== false))
            ? 'Forbidden folder'
            : activeOpt ? activeOpt.name : "This file's folder"}
        </span>
        <ChevronDown size={14} className="themed-text-muted shrink-0" />
      </button>
      {open && opts.length > 0 && (
        // Frosted glass menu (ws-menu) — matches every other popover in the app.
        <div className="absolute top-full right-0 mt-1 p-1 ws-menu z-50 min-w-[220px] max-h-[280px] overflow-y-auto">
          {orderedAncestors.map((o, i) => {
            const isActive = activeId === o.folderId
            // The drive's own folders (the drive root, Library, a person's space…) are
            // listed for orientation but can't be chosen: an app link would serve the
            // whole of one (#889). The server refuses them too.
            const refused = o.shareable === false
            const structural = refused ? structuralFolderKind(o.relPath.split('/').filter(Boolean)) : null
            return (
              <button
                key={o.folderId}
                type="button"
                disabled={refused}
                title={refused ? "One of the drive's own folders. An app link can't serve it." : undefined}
                onClick={() => {
                  setOpen(false)
                  // Always emit the explicit folder id. The previous
                  // default-as-null shorthand broke EditPanel's dirty
                  // check when the saved root happened to equal the
                  // default (reverting picked-null vs saved-id never
                  // matched, so the warning stayed pinned open).
                  onChange(o.folderId)
                }}
                className={`ws-menu-item w-full ${isActive ? '' : 'themed-text-secondary'} ${refused ? 'opacity-40 cursor-not-allowed' : ''}`}
                style={{ paddingLeft: `${12 + i * 14}px` }}
                data-structural={refused || undefined}
              >
                <span className="inline-block w-3 text-center text-[10px] themed-text-muted">
                  {isActive ? '✓' : ''}
                </span>
                {structural ? <FileIcon isFolder structural={structural} size={12} /> : <Folder size={12} className="themed-text-muted shrink-0" />}
                <span className="truncate">{o.name}</span>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Phase 17 (Decision 8c): unified recipient list with per-row checkboxes,
 * batch [Email All / Email Selected], inline add, and per-row remove.
 * Used in both the post-create transition view AND the Edit panel.
 *
 * Server-truth: the `emails` prop is the link's current allowed_emails
 * list. Add/remove issue an immediate PATCH so the list always reflects
 * what's saved on the share row. Send actions are independent of list
 * mutations.
 */
/**
 * Track container width via ResizeObserver. Used to switch action buttons
 * to icon-only when the recipient list is rendered in a narrow surface
 * (e.g., share dialog on small screens, or future side-panel embed).
 */
function useContainerWidth<T extends HTMLElement>(): { ref: RefObject<T | null>; width: number } {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setWidth(entry.contentRect.width)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return { ref, width }
}

const COMPACT_BREAKPOINT_PX = 420

/**
 * DEV-ONLY callout under a recipient row showing the click-through magic URL.
 * In dev, `EMAIL_MODE=mock` writes the email to `_test_emails` and nothing
 * reaches a real inbox, so the developer needs the URL surfaced here to test
 * the recipient flow. The server only includes `devMagicUrl` in the send
 * response when `NODE_ENV !== 'production'`, so this never renders in prod.
 *
 * Copy, NOT a clickable link: opening it in THIS browser would sign the
 * owner's own session in as the recipient. The hint tells you to paste it
 * into a private window instead.
 */
function DevMagicCallout({ url }: { url: string }) {
  const [copied, setCopied] = useState(false)
  const copy = useCallback(() => {
    navigator.clipboard?.writeText(url)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
      .catch(() => {})
  }, [url])
  return (
    <div className="mt-1.5 ml-12 rounded-md border border-amber-500/30 bg-amber-500/10 px-2.5 py-1.5">
      <div className="text-[10px] font-medium text-amber-300/90 uppercase tracking-wide">
        Dev — email not sent · single-use · 24h
      </div>
      <div className="mt-1 flex items-center gap-1.5">
        <code className="flex-1 min-w-0 truncate font-mono text-[10px] themed-text-muted" title={url}>{url}</code>
        <button
          type="button"
          onClick={copy}
          className="shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] themed-btn-secondary"
          title="Copy the magic link"
        >
          {copied ? <Check size={10} /> : <Copy size={10} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="mt-1 text-[10px] themed-text-muted">
        Open in a private window to sign in as this recipient.
      </div>
    </div>
  )
}

function RecipientListBlock({
  linkId,
  emails,
  grants,
  isFolder,
  isAppShare,
  canGrantEdit,
  editBlockedRows,
  markBlocked,
  clearBlocked,
  onAdd,
  onRemove,
  onPermissionChange,
  hasWildcard,
  strictAudience = true,
  idPrefix,
  ensureSaved,
  showAddEmailCheckbox,
  notifyEmails,
  setNotifyEmail,
}: {
  /** null in Create mode (link not minted yet) — Send Email buttons hide
   *  until linkId is set. Edit / post-create modes pass link.id. */
  linkId: string | null
  emails: string[]
  // 0083: per-recipient grant carries permission + liveMode.
  grants: RecipientGrants
  isFolder: boolean
  isAppShare: boolean
  canGrantEdit: boolean
  editBlockedRows: Set<string>
  markBlocked: (key: string) => void
  clearBlocked: (key: string) => void
  /** Add a new recipient. Async so the caller can run a server upsert
   *  (Edit / PostCreate) or stage the grant for post-mint email (Create).
   *  Throws to surface validation errors back into the modal. */
  onAdd: (
    email: string,
    grant: RecipientGrant,
    opts: { sendQuickLink: boolean },
  ) => Promise<void>
  onRemove: (email: string) => void
  onPermissionChange: (email: string, sel: PickerSelection) => void
  hasWildcard: boolean
  /** 0249: a strict link with nobody listed opens for its creator only. */
  strictAudience?: boolean
  idPrefix: string
  /** Optional auto-save before send (Edit mode persists any pending
   *  expiry change so the email goes out with the up-to-date link). */
  ensureSaved?: () => Promise<boolean>
  /** When true (Create mode): the AddRecipientModal renders an "Email
   *  quick link" checkbox the user can opt out of; when false (Edit /
   *  PostCreate): the checkbox is hidden and the caller sends the
   *  quick-link email immediately after the upsert resolves. */
  showAddEmailCheckbox: boolean
  /** Create-mode notify-after-mint tracking. When both are provided AND
   *  there's no link yet, each row's permission popover gets a
   *  checkbox-style "Email quick link after create" toggle so the user
   *  can flip the staged intent without re-opening the add modal. */
  notifyEmails?: Set<string>
  setNotifyEmail?: (email: string, send: boolean) => void
}) {
  // Real names for org-member recipients. `undefined` outside the dialog's
  // provider — LinksPage renders this same block — which falls back to the
  // email-derived name, i.e. today's behaviour.
  const memberNames = useMemberNames()
  const toast = useToast()
  const confirm = useConfirm()
  const { productName } = useBrand()
  const { ref: containerRef, width: containerWidth } = useContainerWidth<HTMLDivElement>()
  // 0 means we haven't measured yet (first render); render with full
  // labels then so the post-measurement collapse isn't jarring.
  const compact = containerWidth > 0 && containerWidth < COMPACT_BREAKPOINT_PX
  const [status, setStatus] = useState<Record<string, { state: 'idle' | 'sending' | 'sent' | 'rate-limited' | 'error'; message?: string; lastSentAt?: string | null; devMagicUrl?: string }>>({})
  const [addModalOpen, setAddModalOpen] = useState(false)

  const hasLink = linkId !== null

  type SendResult = { ok: true } | { ok: false; rateLimited: boolean; lastSentAt?: string | null; error?: string }

  const send = useCallback(async (email: string, force: boolean): Promise<SendResult> => {
    if (!linkId) return { ok: false, rateLimited: false }
    setStatus(prev => ({ ...prev, [email]: { state: 'sending' } }))
    const res = await sendShareLinkMagic(linkId, email, { force })
    if (res.ok) {
      setStatus(prev => ({ ...prev, [email]: { state: 'sent', lastSentAt: res.sentAt, devMagicUrl: res.devMagicUrl } }))
      return { ok: true }
    }
    if (res.kind === 'rate-limited') {
      setStatus(prev => ({
        ...prev,
        [email]: {
          state: 'rate-limited',
          message: res.lastSentAt ? `Sent ${formatRelative(res.lastSentAt)}` : 'Sent recently',
          lastSentAt: res.lastSentAt,
        },
      }))
      return { ok: false, rateLimited: true, lastSentAt: res.lastSentAt }
    }
    setStatus(prev => ({ ...prev, [email]: { state: 'error', message: res.error } }))
    return { ok: false, rateLimited: false, error: res.error }
  }, [linkId])

  const handlePerRowSend = useCallback(async (email: string) => {
    // Auto-save any pending link-level edits (e.g. expiry change in
    // EditPanel) so the recipient gets the link with the up-to-date
    // configuration. If the save fails, bail before sending — sending
    // with stale config is worse than not sending.
    if (ensureSaved) {
      const saved = await ensureSaved()
      if (!saved) return
    }
    // Always a non-forced send first. If the 1/hour limit trips, we don't flip
    // the row to "Sent Xm ago" and make the user hunt for a separate "Resend
    // anyway" — we surface an inline confirm right here. Confirming is the ONLY
    // path that forces past the limit.
    const res = await send(email, false)
    if (res.ok) { toast.success(`Sent to ${email}`); return }
    if (res.rateLimited) {
      const when = res.lastSentAt ? formatRelative(res.lastSentAt) : 'recently'
      const resend = await confirm({
        title: 'Already sent recently',
        message: `A quick link was already emailed to ${email} ${when}. ${productName} limits this to once an hour so recipients aren't spammed. Resend anyway?`,
        type: 'warning',
        confirmText: 'Resend anyway',
        cancelText: 'Cancel',
      })
      if (!resend) return
      const forced = await send(email, true)
      if (forced.ok) toast.success(`Resent to ${email}`)
      else if (!forced.ok && forced.error) toast.error(`Couldn't send to ${email}: ${forced.error}`)
      return
    }
    if (res.error) toast.error(`Couldn't send to ${email}: ${res.error}`)
  }, [ensureSaved, send, confirm, toast])

  return (
    <div ref={containerRef} className="rounded-md overflow-hidden ws-dpill-list">
        {emails.length > 0 ? (
          // Cap the list height so a share with many recipients doesn't
          // push the rest of the edit panel (Expires at, Save/Cancel)
          // below the dialog's scroll edge. ~5 rows visible; the rest
          // scroll within the list. The outer dialog body still scrolls
          // independently, which is fine — users can scroll either.
          <div className="flex flex-col max-h-[15rem] overflow-y-auto">
            {emails.map((email) => {
              const s = status[email] ?? { state: 'idle' as const }
              const sending = s.state === 'sending'
              const sent = s.state === 'sent'
              const rateLimited = s.state === 'rate-limited'
              const error = s.state === 'error'
              const editBlocked = editBlockedRows.has(email)
              const grant = grants[email] ?? { permission: 'view' as const, liveMode: false }
              const displayName = recipientDisplayName(email, memberNames)
              // The action label is ALWAYS "Email Quick Link" — never relabeled
              // to "Resend anyway". The 1/hour limit is surfaced by the confirm
              // dialog on click (see handlePerRowSend), not by mutating the
              // button. Every click is a non-forced send; the dialog is the only
              // path that forces past the limit.
              // Blocked = this person gets no access, so inviting them to it
              // is the one send that must never go out — by any route, staged
              // or immediate.
              const isBlockedRow = grant.permission === 'block'
              const sendLabel = sending ? 'Sending…' : 'Email Quick Link'
              const sendDesc = isBlockedRow
                ? 'Blocked recipients are never emailed.'
                : 'Send the recipient a private invite to this link.'
              const rowActions: RowActionItem[] = []
              if (hasLink) {
                rowActions.push({
                  key: 'send',
                  label: sendLabel,
                  icon: sending
                    ? <Loader2 size={11} className="animate-spin" />
                    : <Mail size={11} />,
                  onClick: () => { void handlePerRowSend(email) },
                  disabled: sending || isBlockedRow,
                  desc: sendDesc,
                })
              }
              rowActions.push({
                key: 'remove',
                label: 'Remove access',
                icon: <Trash2 size={11} />,
                onClick: () => onRemove(email),
                danger: true,
                desc: 'Take this person off the list.',
              })
              if (!hasLink && notifyEmails && setNotifyEmail) {
                // Create mode: toggle the staged "email after mint" intent.
                // Rendered as a native <input type="checkbox"> via the
                // 'checkbox' RowActionItem kind — pushed last so the
                // checkbox sits at the bottom of the popover.
                //
                // Blocked rows show it unchecked and disabled. Reading
                // `false` matters as much as refusing the click: the intent
                // may have been staged BEFORE the row was switched to
                // Blocked, and a box still showing a tick would promise an
                // email that (correctly) never arrives. The send itself is
                // filtered at mint time, so this is the honest display of a
                // decision already made elsewhere.
                const notifiable = grant.permission !== 'block'
                rowActions.push({
                  kind: 'checkbox',
                  key: 'notify',
                  label: notifiable ? 'Email quick link on create' : 'Blocked — never emailed',
                  checked: notifiable && notifyEmails.has(email),
                  disabled: !notifiable,
                  onChange: (next) => setNotifyEmail(email, next),
                })
              }
              return (
                <div key={email} className="px-3 py-2">
                  <div className="flex items-center gap-3 text-xs">
                    {/* Initials follow the resolved NAME, not the email, so the
                        same person reads identically here and in the permission
                        dialog. Colour still seeds on the email — that is the one
                        identity key every surface already shares. */}
                    <div className={`w-9 h-9 rounded-full ${avatarBg(email)} flex items-center justify-center text-white text-[11px] font-semibold shrink-0 select-none`}>
                      {getInitials(displayName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="themed-text font-medium truncate" title={displayName}>{displayName}</div>
                      <div className="themed-text-muted text-[11px] truncate" title={email}>{email}</div>
                    </div>
                    {sent && <span className="text-[10px] text-emerald-400 shrink-0">Sent</span>}
                    {rateLimited && <span className="text-[10px] text-yellow-300 shrink-0" title={s.message ?? undefined}>{s.message}</span>}
                    {error && <span className="text-[10px] text-red-400 shrink-0" title={s.message ?? undefined}>{s.message}</span>}
                    {editBlocked && <span className="text-[10px] text-red-400 shrink-0">Manage required</span>}
                    {!isAppShare && (
                      <PermissionPicker
                        permission={grant.permission}
                        // 0083: per-recipient liveMode lives in the grant
                        // entry. Each row's picker writes both permission
                        // and liveMode independently of every other row.
                        liveMode={grant.liveMode}
                        canGrantEdit={canGrantEdit}
                        onChange={(sel) => {
                          if (sel.permission === 'edit' && !canGrantEdit) {
                            markBlocked(email)
                            return
                          }
                          clearBlocked(email)
                          onPermissionChange(email, sel)
                        }}
                        isFolder={isFolder}
                        compact={compact}
                        actions={rowActions}
                        idPrefix={`${idPrefix}-row-${email}`}
                      />
                    )}
                    {isAppShare && (
                      <AppRowActionsMenu
                        idPrefix={`${idPrefix}-row-${email}`}
                        actions={rowActions}
                      />
                    )}
                  </div>
                  {/* DEV ONLY: the magic link never reaches a real inbox in dev
                      (mock mode → _test_emails), so surface a copyable URL here
                      to test the recipient click-through. Server gates this on
                      NODE_ENV, so the field is simply absent in production. */}
                  {s.devMagicUrl && <DevMagicCallout url={s.devMagicUrl} />}
                </div>
              )
            })}
          </div>
        ) : (
          // min-h fits the two-line variant so swapping between the
          // one-line and two-line copy doesn't resize the box.
          <div className="px-2.5 py-3 text-[11px] themed-text-muted text-center min-h-14 flex items-center justify-center">
            {hasWildcard
              ? 'Add email to invite them, or manage their access via the link.'
              : strictAudience
                ? 'No emails listed yet. Only the creator can open this link.'
                // An older (pre-0249) link naming nobody still opens for whoever already has access.
                : 'No emails listed yet. The link opens only for people who already have access — add an email to allow someone new.'}
          </div>
        )}

        <button
          type="button"
          onClick={() => setAddModalOpen(true)}
          className="inline-flex items-center gap-2 px-3 py-2.5 text-sm font-medium themed-text-secondary hover:themed-text themed-bg-hover w-full border-t themed-border"
        >
          <Plus size={16} />
          Add email
        </button>
      <AddRecipientModal
        isOpen={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSubmit={async (email, grant, opts) => {
          // Optional auto-save (Edit mode flushes pending expiry / app-root
          // changes so the recipient gets the link with up-to-date config).
          if (ensureSaved) {
            const saved = await ensureSaved()
            if (!saved) throw new Error('Save failed — recipient not added.')
          }
          await onAdd(email, grant, opts)
          // Edit mode + checkbox kept on: fire the quick-link send through the
          // row's own `send` (not onAdd) so the per-row status — including the
          // dev magic-link callout — populates on the freshly-added row,
          // identical to the row's "Email Quick Link" action. Create mode has
          // no link yet, so `send` no-ops there; those emails fire post-mint
          // and ShareLinksDialog.handleCreate handles their toasts.
          if (linkId && opts.sendQuickLink) {
            const res = await send(email.toLowerCase(), false)
            if (res.ok) toast.success(`Sent quick link to ${email}`)
          } else if (linkId && !opts.sendQuickLink) {
            toast.success(`Added ${email}`)
          }
        }}
        existingEmails={emails}
        isFolder={isFolder}
        isAppShare={isAppShare}
        canGrantEdit={canGrantEdit}
        showEmailCheckbox={showAddEmailCheckbox}
        linkExists={linkId !== null}
      />
    </div>
  )
}

/**
 * App-share rows don't use the PermissionPicker (the only permission is
 * View — set by the App-kind itself). The row actions still need a host,
 * so we render a small kebab button that opens a portal'd menu with the
 * same Send / Remove items the picker would have shown.
 */
function AppRowActionsMenu({
  idPrefix,
  actions,
}: {
  idPrefix: string
  actions: RowActionItem[]
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [open, setOpen] = useState(false)
  const { menuStyle, triggerRef, menuRef } = usePortalMenuPosition(open)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const t = e.target as Node
      if (triggerRef.current?.contains(t)) return
      if (menuRef.current?.contains(t)) return
      setOpen(false)
    }
    const id = requestAnimationFrame(() => window.addEventListener('click', handler))
    return () => {
      cancelAnimationFrame(id)
      window.removeEventListener('click', handler)
    }
  }, [open])

  return (
    <>
      <button
        ref={triggerRef}
        id={`${idPrefix}-app-menu`}
        type="button"
        onClick={() => setOpen(v => !v)}
        className="p-1.5 themed-text-muted hover:themed-text rounded themed-bg-hover shrink-0"
        aria-label="Recipient actions"
        title="Actions"
      >
        <MoreVertical size={14} />
      </button>
      {open && menuStyle && createPortal(
        <div className={themeClass}>
          <div
            ref={menuRef}
            className={`fixed py-0.5 themed-bg-secondary border themed-border-secondary rounded-md shadow-lg ${SHARE_DIALOG_MENU_Z}`}
            style={portalMenuStyle(menuStyle)}
          >
            {actions.map((act) => (
              act.kind === 'checkbox' ? (
                <label
                  key={act.key}
                  className={`flex items-center gap-2 pl-3.5 pr-2 py-1.5 text-[11px] cursor-pointer themed-bg-hover ${act.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={act.checked}
                    disabled={act.disabled}
                    onChange={(e) => act.onChange(e.target.checked)}
                    className="accent-blue-500 w-3.5 h-3.5 shrink-0"
                  />
                  <span className="themed-text-secondary font-medium">{act.label}</span>
                </label>
              ) : (
                <button
                  key={act.key}
                  type="button"
                  disabled={act.disabled}
                  onClick={() => { setOpen(false); act.onClick() }}
                  className={`w-full text-left px-2 py-1.5 text-[11px] cursor-pointer themed-bg-hover disabled:opacity-50 disabled:cursor-not-allowed ${act.danger ? 'text-red-400 hover:text-red-300' : 'themed-text-secondary'}`}
                  title={act.desc}
                >
                  <div className="flex items-center gap-1.5">
                    <span className="inline-block w-[11px] shrink-0" />
                    <span className="shrink-0 inline-flex items-center justify-center" style={{ width: 11, height: 11 }}>
                      {act.icon}
                    </span>
                    <span className="font-medium">{act.label}</span>
                  </div>
                  {act.desc && (
                    <div className="pl-[30px] themed-text-muted leading-tight mt-0.5 text-[10px]">
                      {act.desc}
                    </div>
                  )}
                </button>
              )
            ))}
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

/**
 * Modal for adding a new recipient to a share link.
 *
 * Replaces the previous inline chip-composer flow. Form body matches the
 * per-row picker (permission + liveMode) so the modal looks like the edit
 * panel for an existing recipient — only difference is the trailing
 * "Email quick link" affordance:
 *   - In create mode (`showEmailCheckbox=true`) it's a checkbox the user
 *     can opt out of; intent is staged on the draft and the email is sent
 *     after the link mints.
 *   - In edit / post-create mode (`showEmailCheckbox=false`) the modal
 *     doesn't show the checkbox at all — adding implicitly sends the
 *     quick link immediately (the caller does the send after the upsert
 *     resolves).
 *
 * Themed via `useTheme` directly because the modal portal's out of the
 * dialog's themed wrapper.
 */
function AddRecipientModal({
  isOpen,
  onClose,
  onSubmit,
  existingEmails,
  isFolder,
  isAppShare,
  canGrantEdit,
  showEmailCheckbox,
  linkExists,
}: {
  isOpen: boolean
  onClose: () => void
  /** Throws to surface validation errors in the modal. Resolves on
   *  success → modal closes. */
  onSubmit: (
    email: string,
    grant: RecipientGrant,
    opts: { sendQuickLink: boolean },
  ) => Promise<void>
  existingEmails: string[]
  isFolder: boolean
  isAppShare: boolean
  canGrantEdit: boolean
  showEmailCheckbox: boolean
  /** When true (Edit / PostCreate), the link is already minted and the
   *  checkbox copy reads "now"; when false (Create), the email is queued
   *  for after the link is created. */
  linkExists: boolean
}) {
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const [email, setEmail] = useState('')
  const [grant, setGrant] = useState<RecipientGrant>({ permission: 'view', liveMode: false })
  const [sendQuickLink, setSendQuickLink] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Reset on each open so a previous attempt's email / picker state
  // doesn't leak into the next "Add" click.
  useEffect(() => {
    if (isOpen) {
      setEmail('')
      setGrant({ permission: 'view', liveMode: false })
      setSendQuickLink(true)
      setError(null)
      setSubmitting(false)
    }
  }, [isOpen])

  const handleSubmit = useCallback(async () => {
    const trimmed = email.trim().toLowerCase()
    if (!trimmed) {
      setError('Enter an email address.')
      return
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setError('Enter a valid email address.')
      return
    }
    if (existingEmails.includes(trimmed)) {
      setError(`${trimmed} is already in the list.`)
      return
    }
    // App shares are view-only by design — coerce defensively even though
    // the picker is hidden in that branch.
    const finalGrant: RecipientGrant = isAppShare
      ? { permission: 'view', liveMode: false }
      : grant
    setSubmitting(true)
    setError(null)
    try {
      await onSubmit(trimmed, finalGrant, {
        // Edit / post-create flows always want the email sent (the
        // checkbox isn't even rendered); create flow honors the checkbox.
        // Never for a BLOCKED recipient, whatever the flow decided: the
        // whole point of blocking is that this person gets no access, and
        // mailing them a link to it is the one thing that must not happen.
        // The `: true` branch made edit-mode adds send unconditionally.
        sendQuickLink: finalGrant.permission !== 'block'
          && (showEmailCheckbox ? sendQuickLink : true),
      })
      onClose()
    } catch (err) {
      const text = errorText(err, 'Failed to add recipient.')   // #1316: a dropped answer shows nothing
      if (text !== null) setError(text)
      setSubmitting(false)
    }
  }, [email, grant, existingEmails, isAppShare, showEmailCheckbox, sendQuickLink, onSubmit, onClose])

  if (!isOpen) return null

  return createPortal(
    // IMPORTANT: themeClass goes on the modal CARD, not this outer
    // wrapper. `.theme-dark/.theme-dim/.theme-light` themselves set
    // `background-color: var(--bg-primary)` (see apps/web/src/index.css
    // line 90-93) — applying the class to an `inset-0` element would
    // turn the whole viewport into a solid opaque "backdrop".
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4">
      {/* Semi-transparent backdrop: dims the share-links dialog beneath
          so the modal reads as foregrounded, while still letting the
          dialog show through. */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        role="button"
        tabIndex={-1}
        aria-label="Close"
        onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}
      />
      <div className={`relative ${themeClass} themed-bg-secondary rounded-lg shadow-2xl ring-1 ring-black/10 max-w-md w-full overflow-hidden`}>
        <div className="flex items-center justify-between px-4 py-3 border-b themed-border">
          <h4 className="text-sm font-semibold themed-text">Add person</h4>
          <button
            type="button"
            onClick={onClose}
            className="p-1 themed-text-muted hover:themed-text rounded"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label htmlFor="add-recipient-modal-email" className="block text-xs font-medium themed-text mb-1.5">
              Email address
            </label>
            <input
              id="add-recipient-modal-email"
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(null) }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); void handleSubmit() }
                else if (e.key === 'Escape') onClose()
              }}
              placeholder="alice@example.com"
              className="w-full themed-input border themed-border rounded px-2.5 py-2 text-sm outline-none focus:border-blue-500"
              autoFocus
              disabled={submitting}
            />
          </div>
          {!isAppShare && (
            <div>
              <span className="block text-xs font-medium themed-text mb-1.5">Permission</span>
              <PermissionPicker
                permission={grant.permission}
                liveMode={grant.liveMode}
                canGrantEdit={canGrantEdit}
                onChange={(sel) => {
                  if (sel.permission === 'edit' && !canGrantEdit) return
                  if (sel.permission === 'edit') {
                    setGrant({ permission: 'edit', liveMode: true })
                  } else if (sel.permission === 'view') {
                    setGrant({ permission: 'view', liveMode: sel.liveMode })
                  } else {
                    setGrant({ permission: 'block', liveMode: false })
                  }
                }}
                isFolder={isFolder}
                idPrefix="add-recipient-modal-perm"
                size="sm"
              />
            </div>
          )}
          {showEmailCheckbox && (
            <label className={`flex items-start gap-2 select-none ${grant.permission === 'block' ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
              <input
                type="checkbox"
                // Blocked means "no access" — reading unchecked rather than
                // just refusing to send keeps the box honest about what will
                // happen. Switching the picker to Blocked visibly clears it.
                checked={sendQuickLink && grant.permission !== 'block'}
                onChange={(e) => setSendQuickLink(e.target.checked)}
                disabled={submitting || grant.permission === 'block'}
                className="accent-blue-500 w-3.5 h-3.5 mt-0.5"
              />
              <span className="text-xs themed-text leading-snug">
                {grant.permission === 'block'
                  ? 'Blocked recipients are never emailed.'
                  : linkExists
                    ? 'Email a quick link to this person.'
                    : 'Email a quick link to this person when the link is created.'}
              </span>
            </label>
          )}
          {error && <p className="text-xs text-red-400">{error}</p>}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 themed-bg-sunken">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium themed-text rounded-md themed-bg-hover disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => { void handleSubmit() }}
            disabled={submitting || !email.trim()}
            className="themed-btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md disabled:opacity-50"
          >
            {submitting && <Loader2 size={11} className="animate-spin" />}
            Add
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
